import React, { useState, useMemo, useEffect, useRef, useCallback, useId } from 'react';
import JourneyTabs from './JourneyTabs';
import { themeColor } from '../utils/themeColor';
import ReactDOM from 'react-dom';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceArea,
  ReferenceLine,
} from 'recharts';
import { IconInfo, IconContractRoll, IconHighImpactFolder, IconChevronRight, IconChevronDown, IconFilter, IconLock } from './Icons';
import { chartTheme, useColorMode } from '../hooks/useColorMode';
import {
  getNasdaqRollWeekInfoForDay,
  isNasdaqFuturesContractType,
  isNasdaqRollQuarterMonth,
  nasdaqRollWeekTouchesWeekMonday,
} from '../utils/cmeNasdaqFuturesRoll';
import {
  DAY_ORDER,
  DEFAULT_DISPLAY_FILTER,
  isDisplayFilterActive,
  dayPassesDisplayFilter,
  uniqueMonthKeysFromStrategyRows,
  RED_FOLDER_FILTER_ENABLED,
} from '../utils/strategyDisplayFilter';
import { PnlScalingInfoModal } from './PnlScalingInfoModal';
import {
  loadUsHighImpactEvents,
  highImpactTouchesWeekMonday,
  monthHasHighImpactEvent,
} from '../utils/usHighImpactCalendar';
import { MULTI_STRAT_SHEET_NAME } from '../utils/vectorCapitalScenario';
import StrategyStatsLockedPreview from './StrategyStatsLockedPreview';
import './StrategyDetailsView.css';
import '../pages/ResultsSpreadsheetPage.css';
import { getDataSourceNote } from '../utils/activeEntity';
import { getBrandIdentity, useBrandIdentity } from '../utils/brandIdentity';

/** Multi-Strat Vector Results: weekday → small label for calendar cell (JS getDay: 0=Sun … 6=Sat). */
function multiStratCalendarStratLabel(jsDayOfWeek) {
  if (jsDayOfWeek === 1 || jsDayOfWeek === 3 || jsDayOfWeek === 5) return 'GFPB 2.1C';
  if (jsDayOfWeek === 2) return 'Tue 2.2A';
  if (jsDayOfWeek === 4) return 'Thur Sp.';
  return null;
}

// ---------- Spreadsheet view helpers (mirrors Results Spreadsheet page) ----------

const SPREADSHEET_NEGATIVE_NUMBER_RE = /^\(?-?\$?\(?-?\d/;
const SPREADSHEET_HEADER_HINT_WORDS = new Set([
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
  'total', 'totals', 'notes', 'date', 'sum', 'summary',
]);

function spreadsheetIsNegativeCell(value) {
  if (value == null) return false;
  const text = String(value).trim();
  if (!text) return false;
  if (text.startsWith('(') && text.endsWith(')')) return true;
  if (text.startsWith('-')) return SPREADSHEET_NEGATIVE_NUMBER_RE.test(text);
  return false;
}

function spreadsheetNormalizeCell(value) {
  return value == null ? '' : String(value);
}

function spreadsheetClassifyRow(row) {
  if (!Array.isArray(row)) return { kind: 'blank', cells: [] };
  const cells = row.map((c) => spreadsheetNormalizeCell(c));
  const trimmed = cells.map((c) => c.trim());
  const filledCount = trimmed.filter((c) => c !== '').length;
  if (filledCount === 0) return { kind: 'blank', cells };

  const firstCell = trimmed[0] || '';
  const restNonEmpty = trimmed.slice(1).filter((c) => c !== '');

  if (firstCell && restNonEmpty.length >= 3) {
    const headerHits = restNonEmpty.filter((c) =>
      SPREADSHEET_HEADER_HINT_WORDS.has(c.toLowerCase())
    ).length;
    if (headerHits / restNonEmpty.length >= 0.6) {
      return { kind: 'header', cells };
    }
  }

  if (!firstCell && restNonEmpty.length >= 2) {
    return { kind: 'subtotal', cells };
  }

  return { kind: 'data', cells };
}

/** Treat a single-cell row 0 followed by a real header in row 1 as a caption. */
function spreadsheetDetectCaption(rows) {
  if (!Array.isArray(rows) || rows.length < 2) return null;
  const r0Cells = (rows[0] || []).map(spreadsheetNormalizeCell).map((c) => c.trim());
  const filled = r0Cells.filter((c) => c !== '').length;
  if (filled !== 1 || !r0Cells[0]) return null;
  const r1NonEmpty = (rows[1] || [])
    .map(spreadsheetNormalizeCell)
    .map((c) => c.trim())
    .filter((c) => c !== '');
  if (r1NonEmpty.length < 3) return null;
  return r0Cells[0];
}

/** Parse a cell into a Number where possible (strips $, commas, %, parens for negatives). */
function spreadsheetSortValue(text) {
  if (text == null) return { num: null, raw: '' };
  const raw = String(text).trim();
  if (!raw) return { num: null, raw: '' };
  // (123) → -123  | -$1,234.5 → -1234.5  | 78.35% → 78.35
  let s = raw;
  let negative = false;
  if (s.startsWith('(') && s.endsWith(')')) {
    negative = true;
    s = s.slice(1, -1);
  }
  if (s.startsWith('-')) {
    negative = true;
    s = s.slice(1);
  }
  s = s.replace(/[$,\s]/g, '');
  const hadPercent = s.endsWith('%');
  if (hadPercent) s = s.slice(0, -1);
  if (s === '' || !/^[+-]?\d*\.?\d+$/.test(s)) {
    return { num: null, raw };
  }
  const n = parseFloat(s);
  if (Number.isNaN(n)) return { num: null, raw };
  return { num: negative ? -n : n, raw };
}

const STORAGE_KEY_VIEW = 'risklo_strategy_performance_view';
const STORAGE_KEY_ROLL = 'risklo_show_roll_week_indicators';
const STORAGE_KEY_HIGH_IMPACT = 'risklo_show_high_impact_usd';
const STORAGE_KEY_IMPACT_FILTER = 'risklo_usd_impact_filter_v2';
const IMPACT_FILTER_OPTIONS = [
  { value: 'ff-red', label: 'FF red folder (High)' },
  { value: 'high,medium', label: 'High + Medium' },
  { value: 'high,medium,low', label: 'High + Med + Low' },
  { value: 'high,medium,low,none', label: 'All USD events' },
];

function normalizeImpactFilterValue(raw) {
  const s = String(raw || '').trim().toLowerCase();
  if (!s) return 'ff-red';
  if (IMPACT_FILTER_OPTIONS.some((x) => x.value === s)) return s;
  return 'ff-red';
}

const SHORT_DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Long-form copy for the roll-week info dialog (legend strip stays one line). */
const getRollWeekModalParagraphs = (appName = getBrandIdentity().appName) => [
  'Futures rollover is when traders and institutions move exposure from the expiring front-month contract to the next listed contract before the exchange’s last trading day for that expiry. For CME E-mini and Micro E-mini Nasdaq-100 (NQ / MNQ), quarterly contracts follow Mar, Jun, Sep, and Dec; last trading day is published by CME (we approximate the calendar week using the 3rd Friday of the contract month).',
  'Around the roll, volume and open interest typically shift from the old front month toward the new one. The expiring contract can become thinner (less liquidity, wider spreads, different slippage) while the new front month absorbs most new activity. That can change how moves behave even if your strategy’s rules are unchanged.',
  `${appName} highlights the calendar week that contains that last trading day so you can compare performance during roll-related conditions versus other weeks. It is context only—not a prediction of how you or the market will behave.`,
];

/** Long-form copy for the USD high-impact (“red folder”) dialog. */
const getHighImpactModalParagraphs = (appName = getBrandIdentity().appName) => [
  'Many economic calendars mark certain U.S. data releases as high impact (often shown as a red folder or similar). Those releases—examples include employment, inflation, GDP, and central-bank decisions—can coincide with wider ranges, faster tape, and gaps around the release time because participation and positioning often pick up.',
  'Volume and liquidity patterns can shift around the release: spreads may widen briefly, and moves may be sharper even when your strategy’s rules are unchanged. That does not mean the market “should” move a certain direction—only that conditions can differ from quieter sessions.',
  `${appName} pulls dates and event names automatically from Forex Factory via the JBlanked calendar API so you can line up performance with days you care about. Times are informational; verify release schedules against an official source. This is context and education only—not trading or investment advice.`,
];

const parseNumber = (value) => {
  if (value === undefined || value === null) return 0;
  const cleaned = value.toString().replace(/[$,]/g, '').trim();
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
};

const formatCurrency = (val, contractType = 'NQ', strategyName = '', contractSize = 1) => {
  if (val === undefined || val === null || isNaN(val)) return '$0';
  
  // Multiply by contract size first
  val = val * contractSize;
  
  // Convert to MNQ if needed (divide by 10), but skip if strategy name contains "micro"
  let num = Number(val) || 0;
  const isMicroStrategy = strategyName.toLowerCase().includes('micro');
  if (contractType === 'MNQ' && !isMicroStrategy) {
    num = num / 10;
  }
  
  const sign = num < 0 ? '-' : '';
  const abs = Math.abs(num);
  
  // For MNQ (non-micro strategies), show 2 decimal places; for NQ or micro strategies, show whole numbers
  const shouldShowDecimals = contractType === 'MNQ' && !isMicroStrategy;
  const formatted = shouldShowDecimals
    ? abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : abs.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  
  return `${sign}$${formatted}`;
};

/** Signed delta for filtered vs baseline PnL; uses same formatter as the main values. */
function formatSignedCurrencyDelta(val, formatCurrencyValue) {
  const core = formatCurrencyValue(Math.abs(val));
  if (val === 0) return core;
  return (val > 0 ? '+' : '-') + core.replace(/^-/, '');
}

function formatPctPointDelta(delta) {
  if (delta === 0) return '0.0%';
  const sign = delta > 0 ? '+' : '';
  return `${sign}${delta.toFixed(1)}%`;
}

function formatSignedIntDelta(delta) {
  if (delta === 0) return '0';
  return (delta > 0 ? '+' : '') + String(delta);
}

/** Small (i) control: hover/focus shows a compact visual key (example numbers, not live data) */
function FilterCompareInfoHint({ align = 'right' }) {
  const tipId = useId();
  return (
    <span
      className={`strategy-filter-compare-info strategy-filter-compare-info--align-${align}`}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="strategy-filter-compare-info-btn"
        aria-label="Visual key: filtered total, unfiltered total, difference, day counts (example figures)"
        aria-describedby={tipId}
      >
        <IconInfo size={12} />
      </button>
      <span id={tipId} className="strategy-filter-compare-info-popover" role="tooltip">
        <span className="strategy-filter-compare-info-title">What each line is</span>
        <div className="strategy-filter-compare-info-demo">
          <div className="strategy-filter-compare-info-demo-row">
            <span className="strategy-filter-compare-info-demo-val strategy-filter-compare-info-demo-val--hero positive">$4,175</span>
            <span className="strategy-filter-compare-info-demo-tag">Filtered</span>
          </div>
          <div className="strategy-filter-compare-info-demo-row">
            <span className="strategy-filter-compare-info-demo-val muted">vs $1,250 all</span>
            <span className="strategy-filter-compare-info-demo-tag">Unfiltered</span>
          </div>
          <div className="strategy-filter-compare-info-demo-row">
            <span className="strategy-filter-compare-info-demo-val positive">+$2,925</span>
            <span className="strategy-filter-compare-info-demo-tag">Difference</span>
          </div>
          <div className="strategy-filter-compare-info-demo-row">
            <span className="strategy-filter-compare-info-demo-val muted small">3 / 5 days</span>
            <span className="strategy-filter-compare-info-demo-tag">Filtered days / days with trades</span>
          </div>
        </div>
        <p className="strategy-filter-compare-info-foot">Example figures only.</p>
      </span>
    </span>
  );
}

const ordinalSuffix = (n) => {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
};

const formatWeekLabel = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('.');
  if (parts.length !== 3) return dateStr;
  const [mm, dd, yyyy] = parts.map((p) => parseInt(p, 10));
  if (isNaN(mm) || isNaN(dd) || isNaN(yyyy)) return dateStr;
  const months = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'Jun.', 'Jul.', 'Aug.', 'Sep.', 'Oct.', 'Nov.', 'Dec.'];
  const month = months[mm - 1] || '';
  return `Week of ${month} ${ordinalSuffix(dd)}, ${yyyy}`;
};

const EMPTY_WEEK_DAYS = { Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0 };

/** Monday date string (MM.DD.YYYY) for the Mon–Fri week that contains this calendar day */
function mondayDateStringForCalendarDay(year, month0, day) {
  const d = new Date(year, month0, day);
  const dow = d.getDay();
  const mondayOffset = dow === 0 ? -6 : 1 - dow;
  d.setDate(d.getDate() + mondayOffset);
  const mm = d.getMonth() + 1;
  const dd = d.getDate();
  const yyyy = d.getFullYear();
  return `${String(mm).padStart(2, '0')}.${String(dd).padStart(2, '0')}.${yyyy}`;
}

function compareMMDDYYYYWeekAsc(a, b) {
  const [mma, dda, yya] = a.split('.').map(Number);
  const [mmb, ddb, yyb] = b.split('.').map(Number);
  const va = yya * 10000 + mma * 100 + dda;
  const vb = yyb * 10000 + mmb * 100 + ddb;
  return va - vb;
}

/**
 * Monday keys for weeks that have at least one Mon–Fri session in this month (so partial weeks
 * at month boundaries appear, and weeks with $0 still show for red-folder / week peek).
 */
function mondayKeysOverlappingMonth(year, month0) {
  const lastDay = new Date(year, month0 + 1, 0).getDate();
  const keys = new Set();
  for (let day = 1; day <= lastDay; day++) {
    const d = new Date(year, month0, day);
    const dow = d.getDay();
    if (dow === 0 || dow === 6) continue;
    keys.add(mondayDateStringForCalendarDay(year, month0, day));
  }
  return [...keys].sort(compareMMDDYYYYWeekAsc);
}

function monthKeyFromYearMonthIndex0(year, monthIndex0) {
  return `${year}-${String(monthIndex0 + 1).padStart(2, '0')}`;
}

/** Prev / current / next month (YYYY-MM) so Mon–Fri weeks that span month boundaries still have red-folder data */
function monthKeysAdjacentForSpillover(year, monthIndex0) {
  const prev = monthIndex0 === 0 ? { y: year - 1, m: 11 } : { y: year, m: monthIndex0 - 1 };
  const next = monthIndex0 === 11 ? { y: year + 1, m: 0 } : { y: year, m: monthIndex0 + 1 };
  return [
    monthKeyFromYearMonthIndex0(prev.y, prev.m),
    monthKeyFromYearMonthIndex0(year, monthIndex0),
    monthKeyFromYearMonthIndex0(next.y, next.m),
  ];
}

function monthKeysForCalendarYear(year) {
  return Array.from({ length: 12 }, (_, i) => monthKeyFromYearMonthIndex0(year, i));
}

function mergeHighImpactEventMaps(maps) {
  const merged = new Map();
  for (const m of maps) {
    if (!m) continue;
    for (const [dateKey, rows] of m.entries()) {
      const existing = merged.get(dateKey);
      if (existing) merged.set(dateKey, existing.concat(rows));
      else merged.set(dateKey, rows);
    }
  }
  return merged;
}

const formatMonthLabel = (monthKey) => {
  if (!monthKey) return '--';
  const parts = monthKey.split('.');
  if (parts.length !== 2) return monthKey;
  const [mm, yyyy] = parts.map((p) => parseInt(p, 10));
  if (isNaN(mm) || isNaN(yyyy)) return monthKey;
  // Short form to save space in KPI cards, e.g. "Feb '26"
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[mm - 1] || '';
  return `${month} '${String(yyyy).slice(-2)}`;
};

/** Shorten a 4-digit year to 2 digits in a day-date string, e.g.
 *  "03.16.2026" → "03.16.26", "04/21/2025" → "04/21/25". */
const shortenDateYear = (dateStr) => {
  if (dateStr == null) return dateStr;
  return String(dateStr).replace(/\b(?:19|20)(\d{2})\b/g, '$1');
};

function formatLtdTooltip(date) {
  if (!date) return '';
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${m}/${d}/${date.getFullYear()}`;
}

function formatLongDateFromKey(dateKey) {
  const parts = (dateKey || '').split('-');
  if (parts.length !== 3) return dateKey || '';
  const y = parseInt(parts[0], 10);
  const mo = parseInt(parts[1], 10) - 1;
  const d = parseInt(parts[2], 10);
  if (isNaN(y) || isNaN(mo) || isNaN(d)) return dateKey;
  const dt = new Date(y, mo, d);
  return dt.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

function nasdaqQuarterLabel(monthIndex0) {
  const map = { 2: 'March (H)', 5: 'June (M)', 8: 'September (U)', 11: 'December (Z)' };
  return map[monthIndex0] ?? null;
}

function formatEconField(v) {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return String(v);
}

/** True if we should show the detail block (AFC row and/or outcome lines). */
function calendarNewsRowHasDetailBlock(r) {
  if (!r || typeof r !== 'object') return false;
  if ([r.actual, r.forecast, r.previous].some((v) => v !== undefined && v !== null && v !== '')) return true;
  if (r.outcome != null && String(r.outcome).trim() !== '') return true;
  if (r.strength != null && String(r.strength).trim() !== '') return true;
  if (r.quality != null && String(r.quality).trim() !== '') return true;
  return false;
}

/** Same per-week filter math as `filteredMonthWeeksSummary` in StrategyDetailsView (single week). */
function computeFilteredWeekSummaryForWeek(week, displayFilter, highImpactEventsByDate, contractType) {
  if (!isDisplayFilterActive(displayFilter)) return null;
  const parts = (week?.date || '').split('.');
  if (parts.length !== 3) {
    return { filteredTotal: 0, filteredDays: 0, filteredDayPnl: { ...EMPTY_WEEK_DAYS } };
  }
  const mm = parseInt(parts[0], 10);
  const dd = parseInt(parts[1], 10);
  const yyyy = parseInt(parts[2], 10);
  let filteredTotal = 0;
  let filteredDays = 0;
  const filteredDayPnl = { ...EMPTY_WEEK_DAYS };
  const rollFilterEnabled = isNasdaqFuturesContractType(contractType);
  DAY_ORDER.forEach((day, idx) => {
    const d = new Date(yyyy, mm - 1, dd + idx);
    const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const dow = SHORT_DAY_NAMES[d.getDay()];
    const val = week.days[day] ?? 0;
    const inRollWeek = rollFilterEnabled
      ? getNasdaqRollWeekInfoForDay(d.getFullYear(), d.getMonth(), d.getDate()).inRollWeek
      : false;
    if (
      val !== 0 &&
      dayPassesDisplayFilter(dateKey, dow, displayFilter, highImpactEventsByDate, {
        rollFilterEnabled,
        inRollWeek: !!inRollWeek,
      })
    ) {
      filteredTotal += val;
      filteredDays += 1;
      filteredDayPnl[day] = val;
    } else {
      filteredDayPnl[day] = 0;
    }
  });
  return { filteredTotal, filteredDays, filteredDayPnl };
}

/** Aligns with `weekPeekDetail` in StrategyCalendarView (weekly sidebar / week peek). */
function buildWeekPeekDetailFromWeekObject(w, fw, showHighImpactUsd, highImpactEventsByDate) {
  if (!w?.date) return null;
  const parts = (w.date || '').split('.');
  if (parts.length !== 3) return null;
  const mm = parseInt(parts[0], 10);
  const dd = parseInt(parts[1], 10);
  const yyyy = parseInt(parts[2], 10);
  const baselineDaysCount = DAY_ORDER.filter((d) => (w.days[d] ?? 0) !== 0).length;
  const weekDays = DAY_ORDER.map((dayName, idx) => {
    const d = new Date(yyyy, mm - 1, dd + idx);
    const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const pnl = w.days[dayName] ?? 0;
    const filteredPnl = fw ? fw.filteredDayPnl?.[dayName] ?? 0 : pnl;
    const hiRows = showHighImpactUsd ? highImpactEventsByDate?.get(dateKey) : null;
    return { dayName, date: d, dateKey, pnl, filteredPnl, hiRows };
  });
  return {
    label: formatWeekLabel(w.date),
    total: fw ? fw.filteredTotal : w.total,
    baselineTotal: w.total,
    daysCount: fw ? fw.filteredDays : baselineDaysCount,
    baselineDaysCount: baselineDaysCount,
    showFilterCompare: !!fw,
    weekDays,
  };
}

function StrategyWeekPeekPortal({
  weekPeekDetail,
  onClose,
  formatCurrencyValue,
  expandedNewsRows,
  setExpandedNewsRows,
  showHighImpactUsd,
}) {
  if (!weekPeekDetail) return null;
  return ReactDOM.createPortal(
    <div
      className="strategy-calendar-day-peek-overlay"
      onClick={onClose}
      role="presentation"
    >
      <aside
        className="strategy-calendar-day-peek"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="strategy-calendar-week-peek-title"
      >
        <div className="strategy-calendar-day-peek-header">
          <h2 id="strategy-calendar-week-peek-title" className="strategy-calendar-day-peek-title">
            {weekPeekDetail.label}
          </h2>
          <button type="button" className="strategy-calendar-day-peek-close" onClick={onClose} aria-label="Close">
            Close
          </button>
        </div>
        <div className="strategy-calendar-day-peek-body strategy-calendar-week-peek-body">
          <section className="strategy-calendar-day-peek-section">
            <h3 className="strategy-calendar-day-peek-section-title strategy-calendar-day-peek-section-title--with-info">
              Weekly Total
              {weekPeekDetail.showFilterCompare ? <FilterCompareInfoHint align="left" /> : null}
            </h3>
            <div className="strategy-calendar-day-peek-prop">
              <span className="strategy-calendar-day-peek-prop-label strategy-calendar-day-peek-prop-label--with-info">
                Net P&amp;L
                {weekPeekDetail.showFilterCompare ? <FilterCompareInfoHint align="left" /> : null}
              </span>
              <div className="strategy-calendar-week-peek-value-stack">
                <span className={`strategy-calendar-day-peek-prop-value ${weekPeekDetail.total >= 0 ? 'positive' : 'negative'}`}>
                  {formatCurrencyValue(weekPeekDetail.total)}
                </span>
                {weekPeekDetail.showFilterCompare && weekPeekDetail.total !== weekPeekDetail.baselineTotal && (
                  <div className="strategy-calendar-week-peek-filter-block strategy-calendar-week-peek-filter-block--under-value">
                    <span className="strategy-calendar-week-baseline">vs {formatCurrencyValue(weekPeekDetail.baselineTotal)} all</span>
                    <span
                      className={`strategy-calendar-stat-delta ${
                        weekPeekDetail.total - weekPeekDetail.baselineTotal >= 0 ? 'positive' : 'negative'
                      }`}
                    >
                      {formatSignedCurrencyDelta(weekPeekDetail.total - weekPeekDetail.baselineTotal, formatCurrencyValue)}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="strategy-calendar-day-peek-prop">
              <span className="strategy-calendar-day-peek-prop-label strategy-calendar-day-peek-prop-label--with-info">
                Days with data
                {weekPeekDetail.showFilterCompare ? <FilterCompareInfoHint align="left" /> : null}
              </span>
              <div className="strategy-calendar-week-peek-value-stack">
                <span className="strategy-calendar-day-peek-prop-value">
                  {weekPeekDetail.showFilterCompare
                    ? `${weekPeekDetail.daysCount} / ${weekPeekDetail.baselineDaysCount}`
                    : weekPeekDetail.daysCount}
                </span>
                {weekPeekDetail.showFilterCompare && weekPeekDetail.daysCount !== weekPeekDetail.baselineDaysCount && (
                  <div className="strategy-calendar-week-peek-filter-block strategy-calendar-week-peek-filter-block--under-value">
                    <span className="strategy-calendar-week-baseline">vs {weekPeekDetail.baselineDaysCount} all</span>
                    <span
                      className={`strategy-calendar-stat-delta ${
                        weekPeekDetail.daysCount - weekPeekDetail.baselineDaysCount >= 0 ? 'positive' : 'negative'
                      }`}
                    >
                      {formatSignedIntDelta(weekPeekDetail.daysCount - weekPeekDetail.baselineDaysCount)} days
                    </span>
                  </div>
                )}
              </div>
            </div>
          </section>

          {weekPeekDetail.weekDays.map((wd, i) => {
            const displayPnl = weekPeekDetail.showFilterCompare ? wd.filteredPnl : wd.pnl;
            const dayDeltaPnl = wd.filteredPnl - wd.pnl;
            const showDayPnlCompare = weekPeekDetail.showFilterCompare && wd.pnl !== 0;
            const dayExcludedByFilter =
              weekPeekDetail.showFilterCompare && wd.pnl !== 0 && wd.filteredPnl === 0;
            const pnlValueClass =
              dayExcludedByFilter && displayPnl === 0
                ? 'strategy-calendar-day-peek-prop-value strategy-calendar-day-peek-prop-value--filter-excluded'
                : `strategy-calendar-day-peek-prop-value ${displayPnl >= 0 ? 'positive' : 'negative'}`;
            return (
              <React.Fragment key={wd.dateKey}>
                {i > 0 && (
                  <div className="strategy-calendar-week-peek-divider" role="separator" aria-hidden />
                )}
                <section
                  className={`strategy-calendar-day-peek-section strategy-calendar-week-peek-day${
                    dayExcludedByFilter ? ' strategy-calendar-week-peek-day--excluded' : ''
                  }`}
                >
                  <div
                    className={
                      dayExcludedByFilter
                        ? 'strategy-calendar-week-peek-day-body strategy-calendar-week-peek-day-body--dimmed'
                        : 'strategy-calendar-week-peek-day-body'
                    }
                  >
                    <h3 className="strategy-calendar-day-peek-section-title">
                      {wd.dayName} — {wd.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </h3>
                    {wd.pnl !== 0 ? (
                      <div className="strategy-calendar-day-peek-prop">
                        <span className="strategy-calendar-day-peek-prop-label strategy-calendar-day-peek-prop-label--with-info">
                          P&amp;L
                          {showDayPnlCompare ? <FilterCompareInfoHint align="left" /> : null}
                        </span>
                        <div className="strategy-calendar-week-peek-value-stack">
                          <span className={pnlValueClass}>{formatCurrencyValue(displayPnl)}</span>
                          {showDayPnlCompare && (
                            <div className="strategy-calendar-week-peek-filter-block strategy-calendar-week-peek-filter-block--under-value">
                              <span className="strategy-calendar-week-baseline">vs {formatCurrencyValue(wd.pnl)} all</span>
                              <span
                                className={`strategy-calendar-stat-delta ${
                                  dayDeltaPnl === 0 ? 'neutral' : dayDeltaPnl >= 0 ? 'positive' : 'negative'
                                }`}
                              >
                                {formatSignedCurrencyDelta(dayDeltaPnl, formatCurrencyValue)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <p className="strategy-calendar-day-peek-muted">No trades</p>
                    )}
                    {wd.hiRows?.length ? (
                      <ul className="strategy-calendar-day-peek-news-list strategy-calendar-week-peek-news">
                        {wd.hiRows.map((r, ri) => {
                          const showDetail = calendarNewsRowHasDetailBlock(r);
                          const rowKey = `wp-${wd.dateKey}-${ri}|${r.time}|${r.event}`;
                          const isExpanded = !!expandedNewsRows[rowKey];
                          const hasAfc = [r.actual, r.forecast, r.previous].some(
                            (v) => v !== undefined && v !== null && v !== ''
                          );
                          const disp = (v) => formatEconField(v) ?? '—';
                          return (
                            <li key={`${r.time}-${r.event}-${ri}`} className="strategy-calendar-day-peek-news-item">
                              <div className="strategy-calendar-day-peek-news-summary-row">
                                {showDetail ? (
                                  <button
                                    type="button"
                                    className="strategy-calendar-day-peek-expand"
                                    aria-expanded={isExpanded}
                                    aria-label={isExpanded ? 'Hide detail' : 'Show detail'}
                                    onClick={() => {
                                      setExpandedNewsRows((prev) => ({
                                        ...prev,
                                        [rowKey]: !prev[rowKey],
                                      }));
                                    }}
                                  >
                                    <IconChevronRight
                                      size={16}
                                      color="rgba(165, 180, 252, 0.95)"
                                      className={`strategy-calendar-day-peek-chevron${isExpanded ? ' strategy-calendar-day-peek-chevron--open' : ''}`}
                                    />
                                  </button>
                                ) : (
                                  <span className="strategy-calendar-day-peek-expand-spacer" aria-hidden />
                                )}
                                <div className="strategy-calendar-day-peek-news-top">
                                  <div className="strategy-calendar-day-peek-news-main">
                                    <span className="strategy-calendar-day-peek-news-time">{r.time || '—'}</span>
                                    <span className="strategy-calendar-day-peek-news-name">{r.event}</span>
                                  </div>
                                  {r.currency && <span className="strategy-calendar-day-peek-pill">{r.currency}</span>}
                                </div>
                              </div>
                              {showDetail && isExpanded && (
                                <div className="strategy-calendar-day-peek-news-detail">
                                  {hasAfc && (
                                    <div className="strategy-calendar-day-peek-afc-row">
                                      <div className="strategy-calendar-day-peek-afc-cell">
                                        <span className="strategy-calendar-day-peek-afc-label">Actual</span>
                                        <span className="strategy-calendar-day-peek-afc-val">{disp(r.actual)}</span>
                                      </div>
                                      <div className="strategy-calendar-day-peek-afc-cell">
                                        <span className="strategy-calendar-day-peek-afc-label">Forecast</span>
                                        <span className="strategy-calendar-day-peek-afc-val">{disp(r.forecast)}</span>
                                      </div>
                                      <div className="strategy-calendar-day-peek-afc-cell">
                                        <span className="strategy-calendar-day-peek-afc-label">Previous</span>
                                        <span className="strategy-calendar-day-peek-afc-val">{disp(r.previous)}</span>
                                      </div>
                                    </div>
                                  )}
                                  {r.outcome && <p className="strategy-calendar-day-peek-news-outcome">{r.outcome}</p>}
                                  {(r.strength || r.quality) && (
                                    <p className="strategy-calendar-day-peek-news-tags">
                                      {[r.strength, r.quality].filter(Boolean).join(' · ')}
                                    </p>
                                  )}
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    ) : showHighImpactUsd ? (
                      <p className="strategy-calendar-day-peek-muted">No red folder events</p>
                    ) : null}
                  </div>
                </section>
              </React.Fragment>
            );
          })}

          <p className="strategy-calendar-day-peek-muted strategy-calendar-day-peek-footnote" style={{ marginTop: '0.5rem' }}>
            Dive deeper:{' '}
            <a
              href="https://www.forexfactory.com/calendar"
              target="_blank"
              rel="noopener noreferrer"
              className="strategy-calendar-day-peek-link"
            >
              Forex Factory calendar
            </a>
          </p>
        </div>
      </aside>
    </div>,
    document.body
  );
}

/** Shared by calendar nav and Vector Results top bar — same state via props. */
export function StrategyFilterCompareControl({
  displayFilter,
  onDisplayFilterChange,
  rollEnabled = false,
  wrapClassName = 'strategy-calendar-filter-wrap',
}) {
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const filterBtnRef = useRef(null);
  const filterPopoverRef = useRef(null);

  const filterActive = isDisplayFilterActive(displayFilter);

  const toggleFilterDay = useCallback(
    (day) => {
      if (!displayFilter || !onDisplayFilterChange) return;
      onDisplayFilterChange({
        ...displayFilter,
        days: { ...displayFilter.days, [day]: !displayFilter.days[day] },
      });
    },
    [displayFilter, onDisplayFilterChange],
  );

  const toggleRedFolderOnly = useCallback(() => {
    if (!displayFilter || !onDisplayFilterChange) return;
    onDisplayFilterChange({ ...displayFilter, redFolderOnly: !displayFilter.redFolderOnly });
  }, [displayFilter, onDisplayFilterChange]);

  const toggleNonRedFolderOnly = useCallback(() => {
    if (!displayFilter || !onDisplayFilterChange) return;
    onDisplayFilterChange({ ...displayFilter, nonRedFolderOnly: !displayFilter.nonRedFolderOnly });
  }, [displayFilter, onDisplayFilterChange]);

  const toggleRolloverOnly = useCallback(() => {
    if (!displayFilter || !onDisplayFilterChange) return;
    onDisplayFilterChange({ ...displayFilter, rolloverOnly: !displayFilter.rolloverOnly });
  }, [displayFilter, onDisplayFilterChange]);

  const toggleNonRolloverOnly = useCallback(() => {
    if (!displayFilter || !onDisplayFilterChange) return;
    onDisplayFilterChange({ ...displayFilter, nonRolloverOnly: !displayFilter.nonRolloverOnly });
  }, [displayFilter, onDisplayFilterChange]);

  const resetDisplayFilter = useCallback(() => {
    if (!onDisplayFilterChange) return;
    onDisplayFilterChange({ ...DEFAULT_DISPLAY_FILTER, days: { ...DEFAULT_DISPLAY_FILTER.days } });
  }, [onDisplayFilterChange]);

  useEffect(() => {
    if (!filterPopoverOpen) return undefined;
    const onClick = (e) => {
      if (
        filterPopoverRef.current &&
        !filterPopoverRef.current.contains(e.target) &&
        filterBtnRef.current &&
        !filterBtnRef.current.contains(e.target)
      ) {
        setFilterPopoverOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setFilterPopoverOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [filterPopoverOpen]);

  if (!displayFilter || !onDisplayFilterChange) return null;

  return (
    <div className={wrapClassName}>
      <button
        ref={filterBtnRef}
        type="button"
        className={`strategy-calendar-filter-btn${filterActive ? ' strategy-calendar-filter-btn--active' : ''}`}
        onClick={() => setFilterPopoverOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={filterPopoverOpen}
        aria-label="Filter & Compare"
        title="Filter & Compare"
      >
        <IconFilter size={14} />
        <span className="strategy-calendar-filter-btn-label strategy-calendar-filter-btn-label--full">Filter &amp; Compare</span>
        <span className="strategy-calendar-filter-btn-label strategy-calendar-filter-btn-label--short">Filter</span>
        {filterActive && <span className="strategy-calendar-filter-dot" />}
      </button>
      {filterPopoverOpen && (
        <div ref={filterPopoverRef} className="strategy-calendar-filter-popover">
          <div className="strategy-calendar-filter-section">
            <span className="strategy-calendar-filter-section-title">Day of Week</span>
            {DAY_ORDER.map((d) => (
              <label key={d} className="strategy-calendar-filter-check">
                <input
                  type="checkbox"
                  checked={displayFilter.days[d]}
                  onChange={() => toggleFilterDay(d)}
                />
                <span>{d === 'Mon' ? 'Monday' : d === 'Tue' ? 'Tuesday' : d === 'Wed' ? 'Wednesday' : d === 'Thu' ? 'Thursday' : 'Friday'}</span>
              </label>
            ))}
          </div>
          {RED_FOLDER_FILTER_ENABLED && (
          <div className="strategy-calendar-filter-section">
            <span className="strategy-calendar-filter-section-title">Calendar Events</span>
            <label className="strategy-calendar-filter-check">
              <input
                type="checkbox"
                checked={displayFilter.redFolderOnly}
                onChange={toggleRedFolderOnly}
              />
              <span>Red folder days only</span>
            </label>
            <label className="strategy-calendar-filter-check">
              <input
                type="checkbox"
                checked={displayFilter.nonRedFolderOnly}
                onChange={toggleNonRedFolderOnly}
              />
              <span>Non-red-folder days only</span>
            </label>
          </div>
          )}
          {rollEnabled && (
            <div className="strategy-calendar-filter-section">
              <span className="strategy-calendar-filter-section-title">Roll week (MNQ / NQ)</span>
              <label className="strategy-calendar-filter-check">
                <input
                  type="checkbox"
                  checked={displayFilter.rolloverOnly}
                  onChange={toggleRolloverOnly}
                />
                <span>Rollover days only</span>
              </label>
              <label className="strategy-calendar-filter-check">
                <input
                  type="checkbox"
                  checked={displayFilter.nonRolloverOnly}
                  onChange={toggleNonRolloverOnly}
                />
                <span>Non-rollover days only</span>
              </label>
            </div>
          )}
          {filterActive && (
            <button
              type="button"
              className="strategy-calendar-filter-reset"
              onClick={resetDisplayFilter}
            >
              Reset filters
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function formatCompactWeekLabel(mondayDate) {
  if (!mondayDate) return '';
  const parts = String(mondayDate).split('-');
  if (parts.length !== 3) return String(mondayDate);
  const [yyyy, mm, dd] = parts;
  return `${mm}.${dd}.${yyyy}`;
}

export function StrategyCalendarView({
  calendarMonth,
  setCalendarMonth,
  calendarRange,
  setCalendarRange,
  dailyData,
  monthWeeksSummary,
  calendarMonthStats,
  yearStats,
  formatCurrencyValue,
  contractType = 'NQ',
  showRollWeek = true,
  highImpactEventsByDate = null,
  showHighImpactUsd = true,
  impactFilterValue = 'ff-red',
  onImpactFilterChange = null,
  displayFilter = null,
  onDisplayFilterChange = null,
  filteredCalendarMonthStats = null,
  filteredYearCalendarStats = null,
  filteredMonthWeeksSummary = null,
  /** Vector Results sheet name; when `Multi-Strat`, calendar weekday cells show source-strategy tags. */
  sheetName = null,
  /** Dashboard widgets: shorten nav labels when the widget column is narrow. */
  preferCompactNav = false,
}) {
  const { year, month } = calendarMonth;
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  // Abbreviate the period dropdown labels on mobile so the month picker row
  // fits on one line ("Mo"/"Yr" instead of "This month"/"This year").
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e) => setIsNarrow(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDow = firstDay.getDay(); // 0 = Sun
  const daysInMonth = lastDay.getDate();

  const compactNav = preferCompactNav || isNarrow;

  const goPrev = () => {
    if (calendarRange === 'year') {
      setCalendarMonth({ year: year - 1, month });
    } else {
      if (month === 0) setCalendarMonth({ year: year - 1, month: 11 });
      else setCalendarMonth({ year, month: month - 1 });
    }
  };
  const goNext = () => {
    if (calendarRange === 'year') {
      setCalendarMonth({ year: year + 1, month });
    } else {
      if (month === 11) setCalendarMonth({ year: year + 1, month: 0 });
      else setCalendarMonth({ year, month: month + 1 });
    }
  };
  const goThisMonth = () => {
    const now = new Date();
    setCalendarMonth({ year: now.getFullYear(), month: now.getMonth() });
  };
  const goThisYear = () => {
    const now = new Date();
    setCalendarMonth({ year: now.getFullYear(), month: now.getMonth() });
  };

  const yearTotal = yearStats?.reduce((sum, m) => sum + m.total, 0) ?? 0;
  const yearDaysWithData = yearStats?.reduce((sum, m) => sum + m.daysWithData, 0) ?? 0;

  const calendarCells = [];
  for (let i = 0; i < startDow; i++) {
    calendarCells.push({ day: null, key: `pad-${i}` });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const dayData = dailyData.get(key);
    calendarCells.push({ day: d, key, data: dayData });
  }

  const handleJumpSelect = (e) => {
    const val = e.target.value;
    if (val === 'this-month') {
      setCalendarRange('month');
      goThisMonth();
    } else if (val === 'this-year') {
      setCalendarRange('year');
      goThisYear();
    }
  };

  const [scalingModalOpen, setScalingModalOpen] = useState(false);
  const [rollExplanationModalOpen, setRollExplanationModalOpen] = useState(false);
  const [highImpactExplanationModalOpen, setHighImpactExplanationModalOpen] = useState(false);
  const [highImpactModalDayKey, setHighImpactModalDayKey] = useState(null);
  const [dayPeekKey, setDayPeekKey] = useState(null);
  const [weekPeekIndex, setWeekPeekIndex] = useState(null);
  /** Which USD news rows in the day peek have forecast/actual detail expanded */
  const [expandedNewsRows, setExpandedNewsRows] = useState({});

  const rollEnabled = showRollWeek && isNasdaqFuturesContractType(contractType);
  const filterActive = isDisplayFilterActive(displayFilter);
  const highImpactEnabled =
    showHighImpactUsd && highImpactEventsByDate && highImpactEventsByDate.size > 0;
  const selectedImpactLabel =
    IMPACT_FILTER_OPTIONS.find((x) => x.value === impactFilterValue)?.label || 'High + Medium (FF parity)';

  const formatHighImpactTitle = (dateKey) => {
    const rows = highImpactEventsByDate?.get(dateKey);
    if (!rows?.length) return undefined;
    return rows.map((r) => `${r.time} ${r.currency} ${r.event}`).join(' · ');
  };

  const highImpactModalParagraphs = useMemo(() => {
    if (highImpactModalDayKey) {
      const rows = highImpactEventsByDate?.get(highImpactModalDayKey);
      if (rows?.length) {
        const detail = rows.map((r) => `${r.time} ${r.event}`).join('; ');
        return [`On this date: ${detail}.`, ...getHighImpactModalParagraphs()];
      }
    }
    return getHighImpactModalParagraphs();
  }, [highImpactModalDayKey, highImpactEventsByDate]);

  const dayPeekDetail = useMemo(() => {
    if (!dayPeekKey) return null;
    const parts = dayPeekKey.split('-').map(Number);
    if (parts.length !== 3 || parts.some((n) => isNaN(n))) return null;
    const [py, pm, pd] = parts;
    const dayData = dailyData.get(dayPeekKey);
    const hiRows = showHighImpactUsd ? highImpactEventsByDate?.get(dayPeekKey) : null;
    const rollInfo = rollEnabled
      ? getNasdaqRollWeekInfoForDay(py, pm - 1, pd)
      : { inRollWeek: false, lastTradingDay: null, quarterMonthIndex0: null };
    const quarterLabel =
      rollInfo.quarterMonthIndex0 != null ? nasdaqQuarterLabel(rollInfo.quarterMonthIndex0) : null;
    return { dayData, hiRows, rollInfo, quarterLabel, py, pm, pd };
  }, [dayPeekKey, dailyData, highImpactEventsByDate, showHighImpactUsd, rollEnabled]);

  const weekPeekDetail = useMemo(() => {
    if (weekPeekIndex == null || !monthWeeksSummary[weekPeekIndex]) return null;
    const w = monthWeeksSummary[weekPeekIndex];
    const fw =
      filterActive && filteredMonthWeeksSummary?.[weekPeekIndex] != null
        ? filteredMonthWeeksSummary[weekPeekIndex]
        : null;
    const parts = (w.mondayDate || '').split('.');
    if (parts.length !== 3) return null;
    const mm = parseInt(parts[0], 10);
    const dd = parseInt(parts[1], 10);
    const yyyy = parseInt(parts[2], 10);
    const weekDays = DAY_ORDER.map((dayName, idx) => {
      const d = new Date(yyyy, mm - 1, dd + idx);
      const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const pnl = w.days[dayName] ?? 0;
      const filteredPnl = fw ? (fw.filteredDayPnl?.[dayName] ?? 0) : pnl;
      const hiRows = showHighImpactUsd ? highImpactEventsByDate?.get(dateKey) : null;
      return { dayName, date: d, dateKey, pnl, filteredPnl, hiRows };
    });
    return {
      label: w.label,
      total: fw ? fw.filteredTotal : w.total,
      baselineTotal: w.total,
      daysCount: fw ? fw.filteredDays : w.daysCount,
      baselineDaysCount: w.daysCount,
      showFilterCompare: !!fw,
      weekDays,
    };
  }, [
    weekPeekIndex,
    monthWeeksSummary,
    filteredMonthWeeksSummary,
    filterActive,
    highImpactEventsByDate,
    showHighImpactUsd,
  ]);

  useEffect(() => {
    if (!dayPeekKey && weekPeekIndex == null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { setDayPeekKey(null); setWeekPeekIndex(null); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [dayPeekKey, weekPeekIndex]);

  useEffect(() => {
    if (!dayPeekKey && weekPeekIndex == null) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [dayPeekKey, weekPeekIndex]);

  useEffect(() => {
    setExpandedNewsRows({});
  }, [dayPeekKey, weekPeekIndex]);

  return (
    <div className="strategy-calendar-view">
      <div className="strategy-calendar-main">
        <div className="strategy-calendar-header">
          <div className="strategy-calendar-nav">
            <div className="strategy-calendar-nav-primary">
              <div className="strategy-calendar-nav-month-row">
                <button type="button" className="strategy-calendar-nav-btn" onClick={goPrev} aria-label={calendarRange === 'year' ? 'Previous year' : 'Previous month'}>←</button>
                <h4 className="strategy-calendar-title">
                  {calendarRange === 'year' ? year : `${monthNames[month]} ${year}`}
                </h4>
                <button type="button" className="strategy-calendar-nav-btn" onClick={goNext} aria-label={calendarRange === 'year' ? 'Next year' : 'Next month'}>→</button>
              </div>
              <div className="strategy-calendar-nav-controls-row">
                <select
                  className="strategy-calendar-jump-select"
                  value={calendarRange === 'year' ? 'this-year' : 'this-month'}
                  onChange={handleJumpSelect}
                  aria-label="View period"
                >
                  <option value="this-month">{compactNav ? 'Mo' : 'This month'}</option>
                  <option value="this-year">{compactNav ? 'Yr' : 'This year'}</option>
                </select>
                <StrategyFilterCompareControl
                  displayFilter={displayFilter}
                  onDisplayFilterChange={onDisplayFilterChange}
                  rollEnabled={rollEnabled}
                />
                <span className="strategy-calendar-scaling-hint strategy-calendar-scaling-hint--inline" role="note">
                  <button
                    type="button"
                    className="strategy-calendar-scaling-hint-btn"
                    aria-haspopup="dialog"
                    aria-expanded={scalingModalOpen}
                    aria-label="Open PnL scaling note"
                    onClick={() => setScalingModalOpen(true)}
                  >
                    <IconInfo size={13} />
                  </button>
                  <span className="strategy-calendar-scaling-hint-text">
                    PnL scaled to your contract count; counts are trades, not contracts.
                  </span>
                </span>
              </div>
            </div>
          </div>
          <div className="strategy-calendar-month-stats">
            {calendarRange === 'year' ? (
              <>
                <span className="strategy-calendar-stat-label strategy-calendar-stat-label--with-info">
                  Yearly stats
                  {filterActive && filteredYearCalendarStats ? <FilterCompareInfoHint /> : null}
                </span>
                {filterActive && filteredYearCalendarStats ? (
                  <>
                    <span
                      className={`strategy-calendar-stat-value ${(filteredYearCalendarStats.total ?? 0) >= 0 ? 'positive' : 'negative'}`}
                    >
                      {formatCurrencyValue(filteredYearCalendarStats.total ?? 0)}
                    </span>
                    <span className="strategy-calendar-stat-comparison">
                      <span className="strategy-calendar-stat-vs">vs</span>
                      <span className={`strategy-calendar-stat-baseline ${yearTotal >= 0 ? 'positive' : 'negative'}`}>
                        {formatCurrencyValue(yearTotal)}
                      </span>
                      <span className="strategy-calendar-stat-vs">all</span>
                    </span>
                    <span
                      className={`strategy-calendar-stat-delta ${
                        (filteredYearCalendarStats.total ?? 0) - yearTotal >= 0 ? 'positive' : 'negative'
                      }`}
                    >
                      {formatSignedCurrencyDelta(
                        (filteredYearCalendarStats.total ?? 0) - yearTotal,
                        formatCurrencyValue,
                      )}
                    </span>
                    <span className="strategy-calendar-stat-days">
                      {filteredYearCalendarStats.daysWithData ?? 0} / {yearDaysWithData} days
                    </span>
                  </>
                ) : (
                  <>
                    <span className={`strategy-calendar-stat-value ${yearTotal >= 0 ? 'positive' : 'negative'}`}>
                      {formatCurrencyValue(yearTotal)}
                    </span>
                    <span className="strategy-calendar-stat-days">{yearDaysWithData} days</span>
                  </>
                )}
              </>
            ) : (
              <>
                <span className="strategy-calendar-stat-label strategy-calendar-stat-label--with-info">
                  Monthly stats
                  {filterActive && filteredCalendarMonthStats ? <FilterCompareInfoHint /> : null}
                </span>
                {filterActive && filteredCalendarMonthStats ? (
                  <>
                    <span className={`strategy-calendar-stat-value ${(filteredCalendarMonthStats.total ?? 0) >= 0 ? 'positive' : 'negative'}`}>
                      {formatCurrencyValue(filteredCalendarMonthStats.total ?? 0)}
                    </span>
                    <span className="strategy-calendar-stat-comparison">
                      <span className="strategy-calendar-stat-vs">vs</span>
                      <span className={`strategy-calendar-stat-baseline ${(calendarMonthStats?.total ?? 0) >= 0 ? 'positive' : 'negative'}`}>
                        {formatCurrencyValue(calendarMonthStats?.total ?? 0)}
                      </span>
                      <span className="strategy-calendar-stat-vs">all</span>
                    </span>
                    <span
                      className={`strategy-calendar-stat-delta ${
                        (filteredCalendarMonthStats.total ?? 0) - (calendarMonthStats?.total ?? 0) >= 0
                          ? 'positive'
                          : 'negative'
                      }`}
                    >
                      {formatSignedCurrencyDelta(
                        (filteredCalendarMonthStats.total ?? 0) - (calendarMonthStats?.total ?? 0),
                        formatCurrencyValue,
                      )}
                    </span>
                    <span className="strategy-calendar-stat-days">
                      {filteredCalendarMonthStats.daysWithData ?? 0} / {calendarMonthStats?.daysWithData ?? 0} days
                    </span>
                  </>
                ) : (
                  <>
                    <span className={`strategy-calendar-stat-value ${(calendarMonthStats?.total ?? 0) >= 0 ? 'positive' : 'negative'}`}>
                      {formatCurrencyValue(calendarMonthStats?.total ?? 0)}
                    </span>
                    <span className="strategy-calendar-stat-days">{calendarMonthStats?.daysWithData ?? 0} days</span>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        <PnlScalingInfoModal open={scalingModalOpen} onClose={() => setScalingModalOpen(false)} />
        {rollEnabled && (
          <PnlScalingInfoModal
            open={rollExplanationModalOpen}
            onClose={() => setRollExplanationModalOpen(false)}
            title="Futures roll and contract rollover"
            paragraphs={getRollWeekModalParagraphs()}
          />
        )}
        {highImpactEnabled && (
          <PnlScalingInfoModal
            open={highImpactExplanationModalOpen}
            onClose={() => {
              setHighImpactExplanationModalOpen(false);
              setHighImpactModalDayKey(null);
            }}
            title="USD high-impact calendar days"
            paragraphs={highImpactModalParagraphs}
          />
        )}

        {calendarRange === 'year' ? (
          <div className="strategy-calendar-year-grid">
            {monthNames.map((name, i) => {
              const m = yearStats?.[i];
              const total = m?.total ?? 0;
              const days = m?.daysWithData ?? 0;
              const hasData = days > 0;
              const rollQuarter = rollEnabled && isNasdaqRollQuarterMonth(year, i);
              const hiMonth = highImpactEnabled && monthHasHighImpactEvent(year, i, highImpactEventsByDate);
              return (
                <div
                  key={i}
                  className={`strategy-calendar-year-cell ${hasData ? (total >= 0 ? 'profit' : 'loss') : ''} ${rollQuarter ? 'strategy-calendar-year-cell--roll-quarter' : ''}`}
                  onClick={() => {
                    setCalendarRange('month');
                    setCalendarMonth({ year, month: i });
                  }}
                >
                  <span className="strategy-calendar-year-month-row">
                    <span className="strategy-calendar-year-month">{name}</span>
                    <span className="strategy-calendar-year-badges">
                      {rollQuarter && (
                        <span
                          className="strategy-calendar-roll-badge"
                          title="Quarterly contract month (roll week falls in this month for MNQ/NQ)"
                        >
                          <IconContractRoll size={13} color="rgba(167, 139, 250, 0.95)" />
                        </span>
                      )}
                      {hiMonth && (
                        <span
                          className="strategy-calendar-high-impact-badge"
                          title={`This month includes at least one USD ${selectedImpactLabel} release day (economic calendar)`}
                        >
                          <IconHighImpactFolder size={12} color="#ef4444" />
                        </span>
                      )}
                    </span>
                  </span>
                  <span className={`strategy-calendar-year-value ${hasData ? (total >= 0 ? 'positive' : 'negative') : 'empty'}`}>
                    {hasData ? formatCurrencyValue(total) : '—'}
                  </span>
                  <span className="strategy-calendar-year-days">{days} day{days !== 1 ? 's' : ''}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="strategy-calendar-grid">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
              <div key={d} className="strategy-calendar-day-header">{d}</div>
            ))}
            {calendarCells.map(({ day, key, data }) => {
              if (day === null) {
                return <div key={key} className="strategy-calendar-cell empty" />;
              }
              const isProfit = data?.value >= 0;
              const hasData = data && data.value !== 0;
              const tradeCount = data?.trades ?? 1;
              const cellDate = new Date(year, month, day);
              const cellDow = SHORT_DAY_NAMES[cellDate.getDay()];
              const rollInfo = rollEnabled
                ? getNasdaqRollWeekInfoForDay(year, month, day)
                : { inRollWeek: false, lastTradingDay: null };
              const dimmed =
                filterActive &&
                !dayPassesDisplayFilter(key, cellDow, displayFilter, highImpactEventsByDate, {
                  rollFilterEnabled: rollEnabled,
                  inRollWeek: !!rollInfo.inRollWeek,
                });
              const rollTitle = rollInfo.inRollWeek && rollInfo.lastTradingDay
                ? `CME MNQ/NQ roll week — last trading day ${formatLtdTooltip(rollInfo.lastTradingDay)} (3rd Friday of quarter)`
                : undefined;
              const hiRows = highImpactEnabled ? highImpactEventsByDate.get(key) : null;
              const hasHi = hiRows?.length;
              const hiTitle = hasHi ? formatHighImpactTitle(key) : undefined;
              const multiStratLabel =
                sheetName === MULTI_STRAT_SHEET_NAME
                  ? multiStratCalendarStratLabel(cellDate.getDay())
                  : null;
              const stratTitle = multiStratLabel ? `Source: ${multiStratLabel}` : undefined;
              const cellTitle = [rollTitle, hiTitle, stratTitle].filter(Boolean).join('\n\n') || undefined;
              return (
                <div
                  key={key}
                  className={`strategy-calendar-cell strategy-calendar-cell--day-peek ${hasData ? (isProfit ? 'profit' : 'loss') : ''} ${rollInfo.inRollWeek ? 'strategy-calendar-cell--roll-week' : ''}${dimmed ? ' strategy-calendar-cell--dimmed' : ''}${multiStratLabel ? ' strategy-calendar-cell--multi-strat' : ''}`}
                  title={cellTitle}
                  role="button"
                  tabIndex={0}
                  aria-label={`Open details for ${formatLongDateFromKey(key)}`}
                  onClick={() => setDayPeekKey(key)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setDayPeekKey(key);
                    }
                  }}
                >
                  <span className="strategy-calendar-cell-day-row">
                    <span className="strategy-calendar-cell-day">{day}</span>
                    <span className="strategy-calendar-cell-day-trailing">
                      <span className="strategy-calendar-cell-day-icons" aria-hidden>
                        {rollInfo.inRollWeek && (
                          <span className="strategy-calendar-cell-roll-icon" title={rollTitle}>
                            <IconContractRoll size={13} color="rgba(167, 139, 250, 0.98)" />
                          </span>
                        )}
                        {hasHi && (
                          <span className="strategy-calendar-cell-high-impact-icon" title={hiTitle}>
                            <IconHighImpactFolder size={12} color="#ef4444" />
                          </span>
                        )}
                      </span>
                      {multiStratLabel ? (
                        <span className="strategy-calendar-cell-strat-tag">{multiStratLabel}</span>
                      ) : null}
                    </span>
                  </span>
                  {hasData && (
                    <div className="strategy-calendar-cell-content">
                      <span className={`strategy-calendar-cell-value ${isProfit ? 'positive' : 'negative'}`}>
                        {formatCurrencyValue(data.value)}
                      </span>
                      <span className="strategy-calendar-cell-trades strategy-calendar-cell-trades-meta">
                        {tradeCount === 1
                          ? '1 trade (not contracts)'
                          : `${tradeCount} trades (not contracts)`}
                      </span>
                      <span className="strategy-calendar-cell-win">{isProfit ? '100.0' : '0.0'}%</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {calendarRange === 'month' && (
        <div className="strategy-calendar-sidebar">
          <h4 className="strategy-calendar-sidebar-title strategy-calendar-sidebar-title--with-info">
            Weekly Summary
            {filterActive ? <FilterCompareInfoHint align="left" /> : null}
          </h4>
          {monthWeeksSummary.map((w, i) => (
            <div
              key={w.mondayDate || i}
              className="strategy-calendar-week-item strategy-calendar-week-item--clickable"
              role="button"
              tabIndex={0}
              onClick={() => setWeekPeekIndex(i)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setWeekPeekIndex(i); } }}
            >
              <span className="strategy-calendar-week-label-row">
                <span className="strategy-calendar-week-label">
                  {preferCompactNav ? formatCompactWeekLabel(w.mondayDate) : w.label}
                </span>
                <span className="strategy-calendar-week-badges">
                  {rollEnabled && w.touchesRollWeek && (
                    <span
                      className="strategy-calendar-week-roll-icon"
                      title="This week overlaps CME MNQ/NQ roll week (week of quarterly last trading day)"
                    >
                      <IconContractRoll size={12} color="rgba(167, 139, 250, 0.95)" />
                    </span>
                  )}
                  {highImpactEnabled && w.touchesHighImpact && (
                    <span
                      className="strategy-calendar-week-high-impact-icon"
                      title={`This week includes a USD ${selectedImpactLabel} release day (economic calendar)`}
                    >
                      <IconHighImpactFolder size={12} color="#ef4444" />
                    </span>
                  )}
                </span>
              </span>
              {filterActive && filteredMonthWeeksSummary?.[i] != null ? (
                <>
                  <span className={`strategy-calendar-week-value ${filteredMonthWeeksSummary[i].filteredTotal >= 0 ? 'positive' : 'negative'}`}>
                    {formatCurrencyValue(filteredMonthWeeksSummary[i].filteredTotal)}
                  </span>
                  {filteredMonthWeeksSummary[i].filteredTotal !== w.total && (
                    <>
                      <span className="strategy-calendar-week-baseline">
                        vs {formatCurrencyValue(w.total)} all
                      </span>
                      <span
                        className={`strategy-calendar-stat-delta ${
                          filteredMonthWeeksSummary[i].filteredTotal - w.total >= 0 ? 'positive' : 'negative'
                        }`}
                      >
                        {formatSignedCurrencyDelta(
                          filteredMonthWeeksSummary[i].filteredTotal - w.total,
                          formatCurrencyValue,
                        )}
                      </span>
                    </>
                  )}
                  <span className="strategy-calendar-week-days">
                    {preferCompactNav
                      ? `${filteredMonthWeeksSummary[i].filteredDays}/${w.daysCount ?? 0}d`
                      : `${filteredMonthWeeksSummary[i].filteredDays} / ${w.daysCount ?? 0} days`}
                  </span>
                </>
              ) : (
                <>
                  <span className={`strategy-calendar-week-value ${w.total >= 0 ? 'positive' : 'negative'}`}>
                    {formatCurrencyValue(w.total)}
                  </span>
                  <span className="strategy-calendar-week-days">
                    {preferCompactNav
                      ? `${w.daysCount ?? 0}d`
                      : `${w.daysCount ?? 0} day${(w.daysCount ?? 0) !== 1 ? 's' : ''}`}
                  </span>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {dayPeekKey &&
        dayPeekDetail &&
        ReactDOM.createPortal(
          <div
            className="strategy-calendar-day-peek-overlay"
            onClick={() => setDayPeekKey(null)}
            role="presentation"
          >
            <aside
              className="strategy-calendar-day-peek"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="strategy-calendar-day-peek-title"
            >
              <div className="strategy-calendar-day-peek-header">
                <h2 id="strategy-calendar-day-peek-title" className="strategy-calendar-day-peek-title">
                  {formatLongDateFromKey(dayPeekKey)}
                </h2>
                <button
                  type="button"
                  className="strategy-calendar-day-peek-close"
                  onClick={() => setDayPeekKey(null)}
                  aria-label="Close"
                >
                  Close
                </button>
              </div>
              <div className="strategy-calendar-day-peek-body">
                <section className="strategy-calendar-day-peek-section">
                  <h3 className="strategy-calendar-day-peek-section-title">Trade</h3>
                  {dayPeekDetail.dayData ? (
                    <>
                      <div className="strategy-calendar-day-peek-prop">
                        <span className="strategy-calendar-day-peek-prop-label">Net P&amp;L</span>
                        <span
                          className={`strategy-calendar-day-peek-prop-value ${(dayPeekDetail.dayData.value ?? 0) >= 0 ? 'positive' : 'negative'}`}
                        >
                          {formatCurrencyValue(dayPeekDetail.dayData.value)}
                        </span>
                      </div>
                      <div className="strategy-calendar-day-peek-prop">
                        <span className="strategy-calendar-day-peek-prop-label">Trades</span>
                        <span className="strategy-calendar-day-peek-prop-value">
                          {(dayPeekDetail.dayData.trades ?? 1) === 1
                            ? '1 (not contracts)'
                            : `${dayPeekDetail.dayData.trades} (not contracts)`}
                        </span>
                      </div>
                      <div className="strategy-calendar-day-peek-prop">
                        <span className="strategy-calendar-day-peek-prop-label">Day win rate</span>
                        <span className="strategy-calendar-day-peek-prop-value">
                          {(dayPeekDetail.dayData.value ?? 0) >= 0 ? '100.0%' : '0.0%'}
                        </span>
                      </div>
                    </>
                  ) : (
                    <p className="strategy-calendar-day-peek-muted">No trades on this day in the selected results.</p>
                  )}
                </section>

                {showHighImpactUsd && (
                  <section className="strategy-calendar-day-peek-section">
                    <h3 className="strategy-calendar-day-peek-section-title">USD calendar releases</h3>
                    {!highImpactEventsByDate ? (
                      <p className="strategy-calendar-day-peek-muted">Calendar data loading…</p>
                    ) : dayPeekDetail.hiRows?.length ? (
                      <>
                      <ul className="strategy-calendar-day-peek-news-list">
                        {dayPeekDetail.hiRows.map((r, i) => {
                          const showDetail = calendarNewsRowHasDetailBlock(r);
                          const rowKey = `${i}|${r.time}|${r.event}`;
                          const isExpanded = !!expandedNewsRows[rowKey];
                          const hasAfc = [r.actual, r.forecast, r.previous].some(
                            (v) => v !== undefined && v !== null && v !== ''
                          );
                          const disp = (v) => formatEconField(v) ?? '—';
                          return (
                          <li key={`${r.time}-${r.event}-${i}`} className="strategy-calendar-day-peek-news-item">
                            <div className="strategy-calendar-day-peek-news-summary-row">
                              {showDetail ? (
                                <button
                                  type="button"
                                  className="strategy-calendar-day-peek-expand"
                                  aria-expanded={isExpanded}
                                  aria-label={isExpanded ? 'Hide forecast and release detail' : 'Show forecast and release detail'}
                                  aria-controls={`peek-news-detail-${i}`}
                                  onClick={() => {
                                    setExpandedNewsRows((prev) => ({
                                      ...prev,
                                      [rowKey]: !prev[rowKey],
                                    }));
                                  }}
                                >
                                  <IconChevronRight
                                    size={16}
                                    color="rgba(165, 180, 252, 0.95)"
                                    className={`strategy-calendar-day-peek-chevron${isExpanded ? ' strategy-calendar-day-peek-chevron--open' : ''}`}
                                  />
                                </button>
                              ) : (
                                <span className="strategy-calendar-day-peek-expand-spacer" aria-hidden />
                              )}
                              <div className="strategy-calendar-day-peek-news-top">
                                <div className="strategy-calendar-day-peek-news-main">
                                  <span className="strategy-calendar-day-peek-news-time">{r.time || '—'}</span>
                                  <span className="strategy-calendar-day-peek-news-name">{r.event}</span>
                                  {r.category ? (
                                    <span className="strategy-calendar-day-peek-news-category">{r.category}</span>
                                  ) : null}
                                </div>
                                {r.currency ? (
                                  <span className="strategy-calendar-day-peek-pill">{r.currency}</span>
                                ) : null}
                              </div>
                            </div>
                            {showDetail && isExpanded ? (
                              <div className="strategy-calendar-day-peek-news-detail" id={`peek-news-detail-${i}`}>
                                {hasAfc ? (
                                  <div className="strategy-calendar-day-peek-afc-row">
                                    <div className="strategy-calendar-day-peek-afc-cell">
                                      <span className="strategy-calendar-day-peek-afc-label">Actual</span>
                                      <span className="strategy-calendar-day-peek-afc-val">{disp(r.actual)}</span>
                                    </div>
                                    <div className="strategy-calendar-day-peek-afc-cell">
                                      <span className="strategy-calendar-day-peek-afc-label">Forecast</span>
                                      <span className="strategy-calendar-day-peek-afc-val">{disp(r.forecast)}</span>
                                    </div>
                                    <div className="strategy-calendar-day-peek-afc-cell">
                                      <span className="strategy-calendar-day-peek-afc-label">Previous</span>
                                      <span className="strategy-calendar-day-peek-afc-val">{disp(r.previous)}</span>
                                    </div>
                                  </div>
                                ) : null}
                                {r.outcome ? (
                                  <p className="strategy-calendar-day-peek-news-outcome">{r.outcome}</p>
                                ) : null}
                                {(r.strength || r.quality) ? (
                                  <p className="strategy-calendar-day-peek-news-tags">
                                    {[r.strength, r.quality].filter(Boolean).join(' · ')}
                                  </p>
                                ) : null}
                              </div>
                            ) : null}
                          </li>
                          );
                        })}
                      </ul>
                      </>
                    ) : (
                      <p className="strategy-calendar-day-peek-muted">No USD releases for the selected impact filter on this date.</p>
                    )}
                    <p className="strategy-calendar-day-peek-muted strategy-calendar-day-peek-footnote">
                      Dive deeper:{' '}
                      <a
                        href="https://www.forexfactory.com/calendar"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="strategy-calendar-day-peek-link"
                      >
                        Forex Factory calendar
                      </a>
                      {' '}
                      (filters, actuals, and related context).
                    </p>
                    <button
                      type="button"
                      className="strategy-calendar-day-peek-learn"
                      onClick={() => {
                        setHighImpactModalDayKey(dayPeekKey);
                        setHighImpactExplanationModalOpen(true);
                        setDayPeekKey(null);
                      }}
                    >
                      About high-impact days
                    </button>
                  </section>
                )}

                <section className="strategy-calendar-day-peek-section">
                  <h3 className="strategy-calendar-day-peek-section-title">Contract roll (NQ / MNQ)</h3>
                  {!rollEnabled ? (
                    <p className="strategy-calendar-day-peek-muted">
                      Roll-week highlights apply to CME Nasdaq-100 futures (NQ / MNQ). Current contract type: {contractType}.
                    </p>
                  ) : dayPeekDetail.rollInfo.inRollWeek && dayPeekDetail.rollInfo.lastTradingDay ? (
                    <>
                      <p className="strategy-calendar-day-peek-roll-lead">
                        This day falls in the <strong>MNQ/NQ roll week</strong>
                        {dayPeekDetail.quarterLabel ? ` (${dayPeekDetail.quarterLabel} quarter)` : ''}.
                      </p>
                      <div className="strategy-calendar-day-peek-prop">
                        <span className="strategy-calendar-day-peek-prop-label">Approx. last trading day</span>
                        <span className="strategy-calendar-day-peek-prop-value">
                          {formatLtdTooltip(dayPeekDetail.rollInfo.lastTradingDay)}
                        </span>
                      </div>
                      <p className="strategy-calendar-day-peek-muted strategy-calendar-day-peek-footnote">
                        Week = Sunday–Saturday containing CME last trading day (3rd Friday of the contract month). Verify dates on{' '}
                        <a
                          href="https://www.cmegroup.com/markets/equities/nasdaq/micro-e-mini-nasdaq-100.contractSpecs.html"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="strategy-calendar-day-peek-link"
                        >
                          CME contract specs
                        </a>
                        .
                      </p>
                    </>
                  ) : (
                    <p className="strategy-calendar-day-peek-muted">
                      Not in a CME MNQ/NQ roll week (the calendar week that contains the quarterly last trading day).
                    </p>
                  )}
                  {rollEnabled && (
                    <button
                      type="button"
                      className="strategy-calendar-day-peek-learn"
                      onClick={() => {
                        setRollExplanationModalOpen(true);
                        setDayPeekKey(null);
                      }}
                    >
                      About roll week
                    </button>
                  )}
                </section>
              </div>
            </aside>
          </div>,
          document.body
        )}

      {weekPeekIndex != null && weekPeekDetail && (
        <StrategyWeekPeekPortal
          weekPeekDetail={weekPeekDetail}
          onClose={() => setWeekPeekIndex(null)}
          formatCurrencyValue={formatCurrencyValue}
          expandedNewsRows={expandedNewsRows}
          setExpandedNewsRows={setExpandedNewsRows}
          showHighImpactUsd={showHighImpactUsd}
        />
      )}

    </div>
  );
}

export function StrategyCumulativeChart({
  chartData,
  formatCurrencyFull,
  formatAxisCurrency,
  contractType = 'NQ',
  showRollWeek = true,
  highImpactEventsByDate = null,
  showHighImpactUsd = true,
  /** Full Journey: mark where live trading starts (YYYY-MM-DD). */
  liveSince = null,
}) {
  const chartColors = chartTheme(useColorMode());
  const liveMarkerLabel = useMemo(() => {
    if (!liveSince || !chartData?.length) return null;
    const point = chartData.find((p) => String(p?.date || '') >= liveSince);
    return point && point !== chartData[0] ? point.dateLabel : null;
  }, [liveSince, chartData]);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = () => setIsMobile(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const chartHeight = isMobile ? 260 : 320;
  const chartMargins = isMobile ? { top: 8, right: 4, left: 0, bottom: 8 } : { top: 10, right: 20, left: 10, bottom: 10 };
  const tickFontSize = isMobile ? 10 : 11;

  const rollHighlight = showRollWeek && isNasdaqFuturesContractType(contractType);
  const highImpactHighlight =
    showHighImpactUsd && highImpactEventsByDate && highImpactEventsByDate.size > 0;

  const rollChartOverlays = useMemo(() => {
    if (!rollHighlight || !chartData?.length) return [];
    const segments = [];
    let start = null;
    for (let i = 0; i < chartData.length; i++) {
      const row = chartData[i];
      const [y, m, d] = row.date.split('-').map(Number);
      const inRoll = getNasdaqRollWeekInfoForDay(y, m - 1, d).inRollWeek;
      if (inRoll) {
        if (start === null) start = i;
      } else if (start !== null) {
        segments.push({ start, end: i - 1 });
        start = null;
      }
    }
    if (start !== null) segments.push({ start, end: chartData.length - 1 });
    return segments.map((seg, idx) => {
      const s = chartData[seg.start];
      const e = chartData[seg.end];
      if (seg.start === seg.end) {
        return { kind: 'line', x: s.dateLabel, key: `roll-line-${s.date}-${idx}` };
      }
      return {
        kind: 'area',
        x1: s.dateLabel,
        x2: e.dateLabel,
        key: `roll-area-${s.date}-${e.date}-${idx}`,
      };
    });
  }, [chartData, rollHighlight]);

  const highImpactChartOverlays = useMemo(() => {
    if (!highImpactHighlight || !chartData?.length) return [];
    const segments = [];
    let start = null;
    for (let i = 0; i < chartData.length; i++) {
      const row = chartData[i];
      const inHi = highImpactEventsByDate.has(row.date);
      if (inHi) {
        if (start === null) start = i;
      } else if (start !== null) {
        segments.push({ start, end: i - 1 });
        start = null;
      }
    }
    if (start !== null) segments.push({ start, end: chartData.length - 1 });
    return segments.map((seg, idx) => {
      const s = chartData[seg.start];
      const e = chartData[seg.end];
      if (seg.start === seg.end) {
        return { kind: 'line', x: s.dateLabel, key: `hi-line-${s.date}-${idx}` };
      }
      return {
        kind: 'area',
        x1: s.dateLabel,
        x2: e.dateLabel,
        key: `hi-area-${s.date}-${e.date}-${idx}`,
      };
    });
  }, [chartData, highImpactHighlight, highImpactEventsByDate]);

  const { yTicks, formatYTick } = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return { yTicks: [], formatYTick: (v) => `$${v}` };
    }
    const vals = chartData.map((d) => d.cumulative);
    const min = Math.min(0, ...vals);
    const max = Math.max(0, ...vals);
    const range = Math.max(max - min, 1);
    const step = range <= 500 ? 100 : range <= 2000 ? 500 : range <= 10000 ? 1000 : range <= 50000 ? 5000 : 10000;
    const ticks = [];
    let t = Math.floor(min / step) * step;
    while (t <= max + step) {
      ticks.push(t);
      t += step;
    }
    const yTicks = [...new Set(ticks)].sort((a, b) => a - b);
    const formatYTick = (v) => formatAxisCurrency(v);
    return { yTicks, formatYTick };
  }, [chartData, formatAxisCurrency]);

  if (!chartData || chartData.length === 0) {
    return (
      <div className="strategy-chart-empty">
        <p>No daily data available to display cumulative P&L.</p>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    const dailyColor = (p.dailyPnl ?? 0) >= 0 ? '#22c55e' : '#ef4444';
    let rollNote = null;
    if (rollHighlight && p?.date) {
      const [y, m, d] = p.date.split('-').map(Number);
      if (getNasdaqRollWeekInfoForDay(y, m - 1, d).inRollWeek) {
        rollNote = 'MNQ/NQ roll week (week of quarterly last trading day)';
      }
    }
    const hiRows = highImpactHighlight && p?.date ? highImpactEventsByDate.get(p.date) : null;
    let hiNote = null;
    if (hiRows?.length) {
      const parts = hiRows.map((r) => `${r.time} ${r.event}`);
      hiNote = parts.length > 2 ? `${parts.slice(0, 2).join('; ')}; +${parts.length - 2} more` : parts.join('; ');
    }
    return (
      <div className="strategy-chart-tooltip">
        <div>{p.tooltipDate}</div>
        {rollNote && (
          <div className="strategy-chart-tooltip-roll">{rollNote}</div>
        )}
        {hiNote && (
          <div className="strategy-chart-tooltip-high-impact">USD high-impact: {hiNote}</div>
        )}
        <div style={{ color: themeColor('#667eea'), fontWeight: 600 }}>
          Cumulative: {formatCurrencyFull(p.cumulative)}
        </div>
        <div style={{ color: dailyColor, fontSize: '0.85em', marginTop: '0.25rem' }}>
          That day: {formatCurrencyFull(p.dailyPnl)}
        </div>
      </div>
    );
  };

  const rollLineStrokeW = isMobile ? 22 : 28;
  const hiLineStrokeW = isMobile ? 18 : 24;

  return (
    <div className="strategy-chart-wrapper">
      <div className="strategy-chart-header">
        <h4 className="strategy-chart-title">Daily net cumulative P&L</h4>
        <span className="strategy-chart-info" title="Cumulative profit/loss over time, starting from zero">ⓘ</span>
      </div>
      {rollHighlight && (
        <div className="strategy-chart-roll-hint" role="note">
          <span className="strategy-chart-roll-hint-swatch" aria-hidden />
          <span className="strategy-chart-roll-hint-text">
            Shaded vertical bands: CME MNQ/NQ roll week (week containing quarterly last trading day).
          </span>
        </div>
      )}
      {highImpactHighlight && (
        <div className="strategy-chart-high-impact-hint" role="note">
          <span className="strategy-chart-high-impact-hint-swatch" aria-hidden />
          <span className="strategy-chart-high-impact-hint-text">
            Red-tinted bands: USD high-impact calendar days (via Forex Factory calendar).
          </span>
        </div>
      )}
      <div className="strategy-chart-container">
        <ResponsiveContainer width="100%" height={chartHeight}>
          <AreaChart data={chartData} margin={chartMargins}>
            <defs>
              <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={themeColor('#667eea')} stopOpacity={0.4} />
                <stop offset="100%" stopColor="#22c55e" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} vertical={false} />
            <XAxis
              dataKey="dateLabel"
              stroke={chartColors.axis}
              tick={{ fontSize: tickFontSize, fill: chartColors.tick }}
              tickFormatter={(v) => v}
            />
            <YAxis
              stroke={chartColors.axis}
              tick={{ fontSize: tickFontSize, fill: chartColors.tick }}
              ticks={yTicks}
              tickFormatter={formatYTick}
            />
            <Tooltip content={<CustomTooltip />} />
            {rollHighlight &&
              rollChartOverlays.map((item) =>
                item.kind === 'area' ? (
                  <ReferenceArea
                    key={item.key}
                    x1={item.x1}
                    x2={item.x2}
                    fill="rgba(167, 139, 250, 0.14)"
                    stroke="rgba(167, 139, 250, 0.28)"
                    strokeWidth={1}
                    isFront={false}
                  />
                ) : (
                  <ReferenceLine
                    key={item.key}
                    x={item.x}
                    stroke="rgba(167, 139, 250, 0.35)"
                    strokeWidth={rollLineStrokeW}
                    strokeOpacity={0.45}
                    isFront={false}
                  />
                )
              )}
            {liveMarkerLabel ? (
              <ReferenceLine
                x={liveMarkerLabel}
                stroke="#22c55e"
                strokeDasharray="4 4"
                label={{ value: 'Live →', position: 'insideTopRight', fill: '#22c55e', fontSize: 11 }}
              />
            ) : null}
            {highImpactHighlight &&
              highImpactChartOverlays.map((item) =>
                item.kind === 'area' ? (
                  <ReferenceArea
                    key={item.key}
                    x1={item.x1}
                    x2={item.x2}
                    fill="rgba(239, 68, 68, 0.11)"
                    stroke="rgba(239, 68, 68, 0.22)"
                    strokeWidth={1}
                    isFront={false}
                  />
                ) : (
                  <ReferenceLine
                    key={item.key}
                    x={item.x}
                    stroke="rgba(239, 68, 68, 0.4)"
                    strokeWidth={hiLineStrokeW}
                    strokeOpacity={0.35}
                    isFront={false}
                  />
                )
              )}
            <Area
              type="monotone"
              dataKey="cumulative"
              stroke={themeColor('#667eea')}
              strokeWidth={2}
              fill="url(#chartGradient)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/**
 * Embedded "Spreadsheet" view (Vector Results → Spreadsheet tab).
 *
 * Mirrors the Results Spreadsheet page rendering — caption, sticky header,
 * classified row variants (data/header/subtotal/blank), negative-number
 * coloring — and adds clickable column headers that toggle ascending →
 * descending → unsorted sort on the underlying data rows.
 */
export function SpreadsheetView({ rawRows, sort, onSortChange }) {
  const caption = useMemo(() => spreadsheetDetectCaption(rawRows), [rawRows]);
  const headerRowIdx = caption ? 1 : 0;
  const headerRow = useMemo(
    () => (rawRows[headerRowIdx] ? rawRows[headerRowIdx].map(spreadsheetNormalizeCell) : []),
    [rawRows, headerRowIdx]
  );
  const bodyRowsRaw = useMemo(
    () => (rawRows.length > headerRowIdx + 1 ? rawRows.slice(headerRowIdx + 1) : []),
    [rawRows, headerRowIdx]
  );
  const classifiedBodyRows = useMemo(
    () => bodyRowsRaw.map(spreadsheetClassifyRow),
    [bodyRowsRaw]
  );
  const columnCount = useMemo(() => {
    let max = headerRow.length;
    for (const r of classifiedBodyRows) {
      if (r.cells.length > max) max = r.cells.length;
    }
    return max;
  }, [headerRow, classifiedBodyRows]);

  // Sort applies to non-blank body rows. Empty/non-parseable cells sink to the
  // bottom regardless of direction so a sorted column doesn't get padded with
  // junk at the top.
  const displayRows = useMemo(() => {
    if (sort.column == null || !sort.direction) return classifiedBodyRows;
    const dir = sort.direction === 'desc' ? -1 : 1;
    const indexed = classifiedBodyRows
      .map((row, originalIdx) => ({ row, originalIdx }))
      .filter(({ row }) => row.kind !== 'blank');
    indexed.sort((a, b) => {
      const av = spreadsheetSortValue(a.row.cells[sort.column]);
      const bv = spreadsheetSortValue(b.row.cells[sort.column]);
      const aMissing = av.num == null && av.raw === '';
      const bMissing = bv.num == null && bv.raw === '';
      if (aMissing && bMissing) return a.originalIdx - b.originalIdx;
      if (aMissing) return 1;
      if (bMissing) return -1;
      if (av.num != null && bv.num != null) return (av.num - bv.num) * dir;
      return (
        av.raw.localeCompare(bv.raw, undefined, { numeric: true, sensitivity: 'base' }) * dir
      );
    });
    return indexed.map(({ row }) => row);
  }, [classifiedBodyRows, sort]);

  const handleHeaderClick = useCallback(
    (colIdx) => {
      if (sort.column !== colIdx) {
        onSortChange({ column: colIdx, direction: 'desc' });
        return;
      }
      if (sort.direction === 'desc') {
        onSortChange({ column: colIdx, direction: 'asc' });
        return;
      }
      onSortChange({ column: null, direction: null });
    },
    [sort, onSortChange]
  );

  if (!rawRows || rawRows.length === 0) {
    return <div className="results-spreadsheet-empty">No data to display.</div>;
  }

  return (
    <div className="results-spreadsheet-table-wrap">
      <table className="results-spreadsheet-table">
        {caption && (
          <caption className="results-spreadsheet-caption">{caption}</caption>
        )}
        <thead>
          <tr>
            {Array.from({ length: columnCount }).map((_, idx) => {
              const label = headerRow[idx] ?? '';
              const isSorted = sort.column === idx && sort.direction;
              const indicator = isSorted ? (sort.direction === 'desc' ? ' ↓' : ' ↑') : '';
              return (
                <th
                  key={idx}
                  scope="col"
                  className={`results-spreadsheet-th-sortable ${isSorted ? 'sorted' : ''}`}
                  aria-sort={
                    isSorted ? (sort.direction === 'desc' ? 'descending' : 'ascending') : 'none'
                  }
                >
                  <button
                    type="button"
                    className="results-spreadsheet-sort-btn"
                    onClick={() => handleHeaderClick(idx)}
                    title={`Sort by ${label || `column ${idx + 1}`}`}
                  >
                    <span>{label}</span>
                    <span className="results-spreadsheet-sort-indicator" aria-hidden="true">
                      {indicator}
                    </span>
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {displayRows.map((row, rIdx) => {
            if (row.kind === 'blank') {
              return (
                <tr key={rIdx} className="results-spreadsheet-row-blank" aria-hidden="true">
                  <td colSpan={columnCount}>&nbsp;</td>
                </tr>
              );
            }
            const rowClass = `results-spreadsheet-row results-spreadsheet-row-${row.kind}`;
            return (
              <tr key={rIdx} className={rowClass}>
                {Array.from({ length: columnCount }).map((_, cIdx) => {
                  const value = row.cells[cIdx] ?? '';
                  const negative = row.kind === 'data' && spreadsheetIsNegativeCell(value);
                  return (
                    <td key={cIdx} className={negative ? 'is-negative' : ''}>
                      {value}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function StrategyDetailsView({
  sheetName,
  metrics,
  metricsBaseline = null,
  rawRows,
  rawRowsForCalendar,
  contractType = 'NQ',
  contractSize = 1,
  displayFilter: displayFilterProp,
  onDisplayFilterChange: onDisplayFilterChangeProp,
  /** Vector Results global date range — duplicated under Strategy Performance when Chart is active */
  vectorDateRangeFilter = null,
  onVectorDateRangeChange = null,
  /**
   * Vector Results only: show the same data-source banner as the main results tab (spreadsheet vs NinjaTrader add-on).
   * Omit for embedded contexts (e.g. Portfolio Simulator) where this copy does not apply.
   */
  vectorDataSource = null,
  /** When true, the in-component data notice is suppressed because the parent
   *  renders it inline beside the page heading. */
  hideDataNotice = false,
  /**
   * Strategy is Internal (admin-only). Renders a green "Internal" badge beside
   * the Strategy Performance heading. Only ever true for admins — members
   * never receive internal strategies from the API at all.
   */
  isInternal = false,
  /**
   * When false, the top stats block (best/worst day, totals, weekday win
   * rates) is rendered with a frosted blur and a centered "Upgrade to RiskLo
   * Pro" overlay. Defaults to true so existing callers are unchanged. Calendar /
   * chart / weekly / spreadsheet views below the stats block stay visible.
   */
  viewerIsPro = true,
  /**
   * Click handler for the upgrade CTA shown in the blur overlay. Receives the
   * suggested upgrade message; ResultsDashboard wires this to the global
   * UpgradeModal. No-op when omitted.
   */
  onUpgrade = null,
  /** { liveSince, view, onChange }: Live / Backtested / Full Journey tabs (only when the strategy has a live date). */
  journey = null,
}) {
  const { appName } = useBrandIdentity();
  const [showRollWeekIndicators, setShowRollWeekIndicators] = useState(() => {
    try {
      const s = localStorage.getItem(STORAGE_KEY_ROLL);
      if (s === '0') return false;
    } catch (_) {}
    return true;
  });
  const [showHighImpactUsd, setShowHighImpactUsd] = useState(() => {
    if (!RED_FOLDER_FILTER_ENABLED) return false;
    try {
      const s = localStorage.getItem(STORAGE_KEY_HIGH_IMPACT);
      if (s === '0') return false;
    } catch (_) {}
    return true;
  });
  const [highImpactEventsByDate, setHighImpactEventsByDate] = useState(null);
  const [impactFilterValue] = useState(() => {
    try {
      return normalizeImpactFilterValue(localStorage.getItem(STORAGE_KEY_IMPACT_FILTER));
    } catch (_) {
      return 'ff-red';
    }
  });

  const [viewMode, setViewMode] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_VIEW);
      if (['calendar', 'chart', 'weekly', 'spreadsheet'].includes(stored)) return stored;
    } catch (_) {}
    return 'calendar';
  });
  const [strategyStatsExpanded, setStrategyStatsExpanded] = useState(false);
  // Weekday win-rate cards collapsed by default so only the top 5 KPIs show,
  // keeping the calendar high on the page (TakeProfitTrader-style density).
  const [weekdayStatsExpanded, setWeekdayStatsExpanded] = useState(false);

  useEffect(() => {
    if (!viewerIsPro) {
      setStrategyStatsExpanded(false);
    }
  }, [sheetName, viewerIsPro]); // 'calendar' | 'chart' | 'weekly' | 'spreadsheet'

  // Sort config for the Spreadsheet view — clicking a column header toggles
  // ascending → descending → unsorted (original order).
  const [spreadsheetSort, setSpreadsheetSort] = useState({ column: null, direction: null });
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [calendarRange, setCalendarRange] = useState('month'); // 'month' | 'year'
  const [internalDisplayFilter, setInternalDisplayFilter] = useState(() => ({
    ...DEFAULT_DISPLAY_FILTER,
    days: { ...DEFAULT_DISPLAY_FILTER.days },
  }));
  const isDisplayFilterControlled =
    displayFilterProp !== undefined && onDisplayFilterChangeProp !== undefined;
  const displayFilter = isDisplayFilterControlled ? displayFilterProp : internalDisplayFilter;
  const setDisplayFilter = isDisplayFilterControlled ? onDisplayFilterChangeProp : setInternalDisplayFilter;
  const [rollInfoModalOpen, setRollInfoModalOpen] = useState(false);
  const [highImpactInfoModalOpen, setHighImpactInfoModalOpen] = useState(false);
  /** Weekly grid: open same week side panel as calendar (Monday key = MM.DD.YYYY). */
  const [weeklyViewWeekPeekMonday, setWeeklyViewWeekPeekMonday] = useState(null);
  const [weeklyWeekPeekExpandedNewsRows, setWeeklyWeekPeekExpandedNewsRows] = useState({});

  /** Months needed for chart bands + calendar: filtered strategy range, full sheet, and calendar navigation */
  const highImpactMonthKeys = useMemo(() => {
    const keys = new Set();
    uniqueMonthKeysFromStrategyRows(rawRows).forEach((k) => keys.add(k));
    uniqueMonthKeysFromStrategyRows(
      rawRowsForCalendar && rawRowsForCalendar.length > 0 ? rawRowsForCalendar : rawRows
    ).forEach((k) => keys.add(k));
    const navKeys =
      calendarRange === 'year'
        ? monthKeysForCalendarYear(calendarMonth.year)
        : monthKeysAdjacentForSpillover(calendarMonth.year, calendarMonth.month);
    navKeys.forEach((k) => keys.add(k));
    return Array.from(keys).sort();
  }, [rawRows, rawRowsForCalendar, calendarMonth.year, calendarMonth.month, calendarRange]);

  const needsMergedImpactForFilter = useMemo(
    () =>
      isDisplayFilterActive(displayFilter) &&
      RED_FOLDER_FILTER_ENABLED && (displayFilter.redFolderOnly || displayFilter.nonRedFolderOnly),
    [displayFilter],
  );

  useEffect(() => {
    let cancelled = false;
    const impacts = impactFilterValue.split(',').filter(Boolean);
    const loadFfRed = needsMergedImpactForFilter;
    if (!RED_FOLDER_FILTER_ENABLED || highImpactMonthKeys.length === 0) {
      setHighImpactEventsByDate(new Map());
      return undefined;
    }
    Promise.all(
      highImpactMonthKeys.map((k) =>
        Promise.all([
          loadUsHighImpactEvents(k, impacts),
          loadFfRed ? loadUsHighImpactEvents(k, ['ff-red']) : Promise.resolve(null),
        ]).then(([main, ff]) => ({ main, ff }))
      )
    ).then((results) => {
      if (cancelled) return;
      let merged = mergeHighImpactEventMaps(results.map((r) => r.main));
      if (loadFfRed) {
        const ffMaps = results.map((r) => r.ff).filter(Boolean);
        merged = mergeHighImpactEventMaps([merged, ...ffMaps]);
      }
      setHighImpactEventsByDate(merged);
    });
    return () => {
      cancelled = true;
    };
  }, [highImpactMonthKeys, impactFilterValue, needsMergedImpactForFilter]);

  // Determine column indexes dynamically based on header row (row index 1)
  const headerRow = rawRows[1] || [];
  const idxMonday = headerRow.findIndex((c) => c?.toLowerCase() === 'monday');
  const idxTuesday = headerRow.findIndex((c) => c?.toLowerCase() === 'tuesday');
  const idxWednesday = headerRow.findIndex((c) => c?.toLowerCase() === 'wednesday');
  const idxThursday = headerRow.findIndex((c) => c?.toLowerCase() === 'thursday');
  const idxFriday = headerRow.findIndex((c) => c?.toLowerCase() === 'friday');

  // Fallbacks if headers are not found
  const mondayCol = idxMonday >= 0 ? idxMonday : 1;
  const tuesdayCol = idxTuesday >= 0 ? idxTuesday : 2;
  const wednesdayCol = idxWednesday >= 0 ? idxWednesday : 3;
  const thursdayCol = idxThursday >= 0 ? idxThursday : 4;
  const fridayCol = idxFriday >= 0 ? idxFriday : 5;

  // Parse the raw data to extract weekly performance
  const parseDateValue = (value) => {
    if (!value) return 0;
    const parts = value.split('.');
    if (parts.length !== 3) return 0;
    const [mm, dd, yyyy] = parts.map((p) => parseInt(p, 10));
    if (isNaN(mm) || isNaN(dd) || isNaN(yyyy)) return 0;
    // Create sortable number: YYYYMMDD
    return yyyy * 10000 + mm * 100 + dd;
  };

  // Weekly rows from date-filtered rawRows (matches summary cards); used for Weekly view + chart
  const weeklyData = useMemo(() => {
    const dataRows = rawRows.slice(2);
    return dataRows
      .map((row) => {
        const date = row[0];
        const monday = parseNumber(row[mondayCol]);
        const tuesday = parseNumber(row[tuesdayCol]);
        const wednesday = parseNumber(row[wednesdayCol]);
        const thursday = parseNumber(row[thursdayCol]);
        const friday = parseNumber(row[fridayCol]);
        const total = monday + tuesday + wednesday + thursday + friday;
        return {
          date,
          days: {
            Mon: monday,
            Tue: tuesday,
            Wed: wednesday,
            Thu: thursday,
            Fri: friday,
          },
          total,
        };
      })
      .filter((w) => w.date)
      .sort((a, b) => parseDateValue(b.date) - parseDateValue(a.date));
  }, [rawRows, mondayCol, tuesdayCol, wednesdayCol, thursdayCol, fridayCol]);

  // Calendar uses unfiltered data (rawRowsForCalendar) so date range doesn't affect it
  const calendarSourceRows = rawRowsForCalendar && rawRowsForCalendar.length > 0 ? rawRowsForCalendar : rawRows;
  const weeklyDataForCalendar = useMemo(() => {
    const dataRows = calendarSourceRows.slice(2);
    return dataRows
      .map((row) => {
        const date = row[0];
        const monday = parseNumber(row[mondayCol]);
        const tuesday = parseNumber(row[tuesdayCol]);
        const wednesday = parseNumber(row[wednesdayCol]);
        const thursday = parseNumber(row[thursdayCol]);
        const friday = parseNumber(row[fridayCol]);
        const total = monday + tuesday + wednesday + thursday + friday;
        return {
          date,
          days: { Mon: monday, Tue: tuesday, Wed: wednesday, Thu: thursday, Fri: friday },
          total,
        };
      })
      .filter((w) => w.date)
      .sort((a, b) => parseDateValue(b.date) - parseDateValue(a.date));
  }, [calendarSourceRows, mondayCol, tuesdayCol, wednesdayCol, thursdayCol, fridayCol]);

  const weekByMondayFromCalendar = useMemo(() => {
    const m = new Map();
    weeklyDataForCalendar.forEach((week) => {
      if (!week?.date) return;
      if (!m.has(week.date)) m.set(week.date, week);
    });
    return m;
  }, [weeklyDataForCalendar]);

  // Build daily P&L map for calendar: "YYYY-MM-DD" -> { value, trades }
  const dailyData = useMemo(() => {
    const map = new Map();
    weeklyDataForCalendar.forEach((week) => {
      const parts = (week.date || '').split('.');
      if (parts.length !== 3) return;
      const mm = parseInt(parts[0], 10);
      const dd = parseInt(parts[1], 10);
      const yyyy = parseInt(parts[2], 10);
      if (isNaN(mm) || isNaN(dd) || isNaN(yyyy)) return;
      // week.date is Monday; add days for Mon–Fri
      DAY_ORDER.forEach((day, idx) => {
        const d = new Date(yyyy, mm - 1, dd + idx);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const value = week.days[day] ?? 0;
        if (value !== 0) {
          map.set(key, { value, trades: 1 });
        }
      });
    });
    return map;
  }, [weeklyDataForCalendar]);

  // Chart cumulative series: same date range as Vector Results filter (filtered rawRows only)
  const dailyDataForChart = useMemo(() => {
    const map = new Map();
    weeklyData.forEach((week) => {
      const parts = (week.date || '').split('.');
      if (parts.length !== 3) return;
      const mm = parseInt(parts[0], 10);
      const dd = parseInt(parts[1], 10);
      const yyyy = parseInt(parts[2], 10);
      if (isNaN(mm) || isNaN(dd) || isNaN(yyyy)) return;
      DAY_ORDER.forEach((day, idx) => {
        const d = new Date(yyyy, mm - 1, dd + idx);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const value = week.days[day] ?? 0;
        if (value !== 0) {
          map.set(key, { value, trades: 1 });
        }
      });
    });
    return map;
  }, [weeklyData]);

  /** Net total & day counts for the Vector date range (chart); filtered vs all matches calendar monthly stats */
  const chartDateRangeStats = useMemo(() => {
    const rollFilterEnabled = isNasdaqFuturesContractType(contractType);
    let totalAll = 0;
    let daysAll = 0;
    dailyDataForChart.forEach(({ value }) => {
      totalAll += value;
      daysAll += 1;
    });
    const filterActive = isDisplayFilterActive(displayFilter);
    let totalFiltered = 0;
    let daysFiltered = 0;
    if (filterActive) {
      dailyDataForChart.forEach(({ value }, key) => {
        const [y, m, d] = key.split('-').map(Number);
        const dt = new Date(y, m - 1, d);
        const dow = SHORT_DAY_NAMES[dt.getDay()];
        const inRollWeek = rollFilterEnabled ? getNasdaqRollWeekInfoForDay(y, m - 1, d).inRollWeek : false;
        if (
          dayPassesDisplayFilter(key, dow, displayFilter, highImpactEventsByDate, {
            rollFilterEnabled,
            inRollWeek: !!inRollWeek,
          })
        ) {
          totalFiltered += value;
          daysFiltered += 1;
        }
      });
    }
    return { totalAll, daysAll, totalFiltered, daysFiltered, filterActive };
  }, [dailyDataForChart, displayFilter, highImpactEventsByDate, contractType]);

  /** Weekly grid: filtered row per week (same logic as calendar sidebar / week peek). */
  const weeklyGridDisplay = useMemo(() => {
    const filterOn = isDisplayFilterActive(displayFilter);
    return weeklyData.map((week) => {
      const fw = filterOn
        ? computeFilteredWeekSummaryForWeek(week, displayFilter, highImpactEventsByDate, contractType)
        : null;
      const baselineDaysCount = DAY_ORDER.filter((d) => (week.days[d] ?? 0) !== 0).length;
      return { week, fw, baselineDaysCount };
    });
  }, [weeklyData, displayFilter, highImpactEventsByDate, contractType]);

  // Weekly summary for calendar month (for sidebar): every week with a Mon–Fri in this month,
  // including $0 weeks so users can open the week peek for red-folder / calendar context.
  const monthWeeksSummary = useMemo(() => {
    const { year, month } = calendarMonth;
    const weekByMonday = new Map();
    weeklyDataForCalendar.forEach((week) => {
      if (!week?.date) return;
      if (!weekByMonday.has(week.date)) weekByMonday.set(week.date, week);
    });
    const mondayKeys = mondayKeysOverlappingMonth(year, month);
    return mondayKeys.map((mondayKey) => {
      const week = weekByMonday.get(mondayKey) || {
        date: mondayKey,
        days: { ...EMPTY_WEEK_DAYS },
        total: 0,
      };
      const daysWithData = DAY_ORDER.filter((d) => (week.days[d] ?? 0) !== 0).length;
      const touchesRollWeek =
        isNasdaqFuturesContractType(contractType) && nasdaqRollWeekTouchesWeekMonday(week.date);
      const touchesHighImpact =
        showHighImpactUsd &&
        highImpactEventsByDate &&
        highImpactTouchesWeekMonday(week.date, highImpactEventsByDate);
      return {
        label: formatWeekLabel(week.date),
        total: week.total,
        daysCount: daysWithData,
        touchesRollWeek,
        touchesHighImpact,
        mondayDate: week.date,
        days: week.days,
      };
    });
  }, [weeklyDataForCalendar, calendarMonth, contractType, showHighImpactUsd, highImpactEventsByDate]);

  // Calendar month stats (month in calendarMonth is 0-indexed; key format is YYYY-MM-DD with 1-indexed month)
  const calendarMonthStats = useMemo(() => {
    const { year, month } = calendarMonth;
    let total = 0;
    let daysWithData = 0;
    const expectedMonth = month + 1; // key uses 1-12
    dailyData.forEach(({ value }, key) => {
      const [y, m] = key.split('-').map(Number);
      if (y === year && m === expectedMonth) {
        total += value;
        daysWithData += 1;
      }
    });
    return { total, daysWithData };
  }, [dailyData, calendarMonth]);

  const filteredCalendarMonthStats = useMemo(() => {
    if (!isDisplayFilterActive(displayFilter)) return null;
    const { year, month } = calendarMonth;
    let total = 0;
    let daysWithData = 0;
    const expectedMonth = month + 1;
    const rollFilterEnabled = isNasdaqFuturesContractType(contractType);
    dailyData.forEach(({ value }, key) => {
      const [y, m, d] = key.split('-').map(Number);
      if (y === year && m === expectedMonth) {
        const dt = new Date(y, m - 1, d);
        const dow = SHORT_DAY_NAMES[dt.getDay()];
        const inRollWeek = rollFilterEnabled ? getNasdaqRollWeekInfoForDay(y, m - 1, d).inRollWeek : false;
        if (
          dayPassesDisplayFilter(key, dow, displayFilter, highImpactEventsByDate, {
            rollFilterEnabled,
            inRollWeek: !!inRollWeek,
          })
        ) {
          total += value;
          daysWithData += 1;
        }
      }
    });
    return { total, daysWithData };
  }, [dailyData, calendarMonth, displayFilter, highImpactEventsByDate, contractType]);

  const filteredYearCalendarStats = useMemo(() => {
    if (!isDisplayFilterActive(displayFilter)) return null;
    const y0 = calendarMonth.year;
    let total = 0;
    let daysWithData = 0;
    const rollFilterEnabled = isNasdaqFuturesContractType(contractType);
    dailyData.forEach(({ value }, key) => {
      const [y, m, d] = key.split('-').map(Number);
      if (y !== y0) return;
      const dt = new Date(y, m - 1, d);
      const dow = SHORT_DAY_NAMES[dt.getDay()];
      const inRollWeek = rollFilterEnabled ? getNasdaqRollWeekInfoForDay(y, m - 1, d).inRollWeek : false;
      if (
        dayPassesDisplayFilter(key, dow, displayFilter, highImpactEventsByDate, {
          rollFilterEnabled,
          inRollWeek: !!inRollWeek,
        })
      ) {
        total += value;
        daysWithData += 1;
      }
    });
    return { total, daysWithData };
  }, [dailyData, calendarMonth.year, displayFilter, highImpactEventsByDate, contractType]);

  const filteredMonthWeeksSummary = useMemo(() => {
    if (!isDisplayFilterActive(displayFilter)) return null;
    const { year, month } = calendarMonth;
    const weekByMonday = new Map();
    weeklyDataForCalendar.forEach((week) => {
      if (!week?.date) return;
      if (!weekByMonday.has(week.date)) weekByMonday.set(week.date, week);
    });
    const mondayKeys = mondayKeysOverlappingMonth(year, month);
    const rollFilterEnabled = isNasdaqFuturesContractType(contractType);
    return mondayKeys.map((mondayKey) => {
      const week = weekByMonday.get(mondayKey) || {
        date: mondayKey,
        days: { ...EMPTY_WEEK_DAYS },
        total: 0,
      };
      const parts = (week.date || '').split('.');
      if (parts.length !== 3) {
        return { filteredTotal: 0, filteredDays: 0, filteredDayPnl: { ...EMPTY_WEEK_DAYS } };
      }
      const mm = parseInt(parts[0], 10);
      const dd = parseInt(parts[1], 10);
      const yyyy = parseInt(parts[2], 10);
      let filteredTotal = 0;
      let filteredDays = 0;
      const filteredDayPnl = { ...EMPTY_WEEK_DAYS };
      DAY_ORDER.forEach((day, idx) => {
        const d = new Date(yyyy, mm - 1, dd + idx);
        const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const dow = SHORT_DAY_NAMES[d.getDay()];
        const val = week.days[day] ?? 0;
        const inRollWeek = rollFilterEnabled ? getNasdaqRollWeekInfoForDay(d.getFullYear(), d.getMonth(), d.getDate()).inRollWeek : false;
        if (
          val !== 0 &&
          dayPassesDisplayFilter(dateKey, dow, displayFilter, highImpactEventsByDate, {
            rollFilterEnabled,
            inRollWeek: !!inRollWeek,
          })
        ) {
          filteredTotal += val;
          filteredDays += 1;
          filteredDayPnl[day] = val;
        } else {
          filteredDayPnl[day] = 0;
        }
      });
      return { filteredTotal, filteredDays, filteredDayPnl };
    });
  }, [weeklyDataForCalendar, calendarMonth, displayFilter, highImpactEventsByDate, contractType]);

  const weekPeekDetailFromWeekly = useMemo(() => {
    if (!weeklyViewWeekPeekMonday) return null;
    const w =
      weekByMondayFromCalendar.get(weeklyViewWeekPeekMonday) ||
      weeklyData.find((wk) => wk.date === weeklyViewWeekPeekMonday);
    if (!w) return null;
    const filterActive = isDisplayFilterActive(displayFilter);
    const fw = filterActive
      ? computeFilteredWeekSummaryForWeek(w, displayFilter, highImpactEventsByDate, contractType)
      : null;
    return buildWeekPeekDetailFromWeekObject(w, fw, showHighImpactUsd, highImpactEventsByDate);
  }, [
    weeklyViewWeekPeekMonday,
    weekByMondayFromCalendar,
    weeklyData,
    displayFilter,
    highImpactEventsByDate,
    contractType,
    showHighImpactUsd,
  ]);

  useEffect(() => {
    if (viewMode !== 'weekly') setWeeklyViewWeekPeekMonday(null);
  }, [viewMode]);

  useEffect(() => {
    if (!weeklyViewWeekPeekMonday) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setWeeklyViewWeekPeekMonday(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [weeklyViewWeekPeekMonday]);

  useEffect(() => {
    if (!weeklyViewWeekPeekMonday) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [weeklyViewWeekPeekMonday]);

  useEffect(() => {
    setWeeklyWeekPeekExpandedNewsRows({});
  }, [weeklyViewWeekPeekMonday]);

  // Year stats: total and days for each month (for year view)
  const yearStats = useMemo(() => {
    const { year } = calendarMonth;
    const byMonth = Array.from({ length: 12 }, (_, i) => ({
      monthIndex: i,
      total: 0,
      daysWithData: 0,
    }));
    dailyData.forEach(({ value }, key) => {
      const [y, m] = key.split('-').map(Number);
      if (y === year && m >= 1 && m <= 12) {
        byMonth[m - 1].total += value;
        byMonth[m - 1].daysWithData += 1;
      }
    });
    return byMonth;
  }, [dailyData, calendarMonth]);

  const chartData = useMemo(() => {
    const isMicro = (sheetName || '').toLowerCase().includes('micro');
    const scale = (val) => {
      let num = (val ?? 0) * contractSize;
      if (contractType === 'MNQ' && !isMicro) num /= 10;
      return num;
    };
    const rollFilterEnabled = isNasdaqFuturesContractType(contractType);
    const filterOn = isDisplayFilterActive(displayFilter);
    const entries = Array.from(dailyDataForChart.entries())
      .map(([date, { value }]) => {
        const scaledValue = scale(value);
        let add = scaledValue;
        if (filterOn) {
          const [y, m, d] = date.split('-').map(Number);
          const dt = new Date(y, m - 1, d);
          const dow = SHORT_DAY_NAMES[dt.getDay()];
          const inRollWeek = rollFilterEnabled ? getNasdaqRollWeekInfoForDay(y, m - 1, d).inRollWeek : false;
          const passes = dayPassesDisplayFilter(date, dow, displayFilter, highImpactEventsByDate, {
            rollFilterEnabled,
            inRollWeek: !!inRollWeek,
          });
          add = passes ? scaledValue : 0;
        }
        return { date, scaledValue: add };
      })
      .filter((e) => e.date && !isNaN(e.scaledValue))
      .sort((a, b) => a.date.localeCompare(b.date));
    let cumulative = 0;
    return entries.map(({ date, scaledValue }) => {
      cumulative += scaledValue;
      const [yyyy, mm, dd] = date.split('-');
      const dateLabel = `${mm}/${dd}/${String(yyyy).slice(-2)}`;
      const tooltipDate = `${mm}/${dd}/${yyyy}`;
      return { date, dateLabel, tooltipDate, cumulative, dailyPnl: scaledValue };
    });
  }, [
    dailyDataForChart,
    displayFilter,
    highImpactEventsByDate,
    contractType,
    contractSize,
    sheetName,
  ]);

  const formatExactCurrency = (val) => formatCurrency(val, contractType, sheetName, contractSize);
  const showMetricsCompare =
    isDisplayFilterActive(displayFilter) && metricsBaseline && metrics;
  const worstDayAmount = Number(metrics?.worstDay?.value) || 0;
  const worstMonthAmount = Number(metrics?.worstMonth?.value) || 0;
  const worstDayToneClass = worstDayAmount < 0 ? 'negative-inline' : 'positive-inline';
  const worstMonthToneClass = worstMonthAmount < 0 ? 'negative-inline' : 'positive-inline';
  const baselineWinPct =
    metricsBaseline && metricsBaseline.totalDays > 0
      ? ((metricsBaseline.totalWinningDays || 0) / metricsBaseline.totalDays) * 100
      : 0;
  const formatScaledCurrency = (val) => {
    const num = Number(val) || 0;
    const sign = num < 0 ? '-' : '';
    const abs = Math.abs(num);
    const isMicroStrategy = (sheetName || '').toLowerCase().includes('micro');
    const shouldShowDecimals = contractType === 'MNQ' && !isMicroStrategy;
    const formatted = shouldShowDecimals
      ? abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : abs.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    return `${sign}$${formatted}`;
  };

  const totalWinRatePct =
    metrics?.totalDays > 0
      ? ((metrics?.totalWinningDays || 0) / metrics.totalDays) * 100
      : 0;
  const totalWinRateDeltaPct = totalWinRatePct - baselineWinPct;
  const netFiltered = (metrics?.totalWinsSum || 0) + (metrics?.totalLossSum || 0);
  const netBaselineSum =
    (metricsBaseline?.totalWinsSum || 0) + (metricsBaseline?.totalLossSum || 0);
  const netDelta = netFiltered - netBaselineSum;
  const dayBestDelta =
    (Number(metrics?.bestDay?.value) || 0) - (Number(metricsBaseline?.bestDay?.value) || 0);
  const dayWorstDelta =
    (Number(metrics?.worstDay?.value) || 0) - (Number(metricsBaseline?.worstDay?.value) || 0);
  const monthBestDelta =
    (Number(metrics?.bestMonth?.value) || 0) - (Number(metricsBaseline?.bestMonth?.value) || 0);
  const monthWorstDelta =
    (Number(metrics?.worstMonth?.value) || 0) - (Number(metricsBaseline?.worstMonth?.value) || 0);
  const winWeekDelta =
    (metrics?.winningWeeks || 0) - (metricsBaseline?.winningWeeks || 0);
  const loseWeekDelta =
    (metrics?.losingWeeks || 0) - (metricsBaseline?.losingWeeks || 0);

  return (
    <div className="strategy-details-view">
      {journey?.liveSince ? <JourneyTabs liveSince={journey.liveSince} view={journey.view} onChange={journey.onChange} /> : null}
      {vectorDataSource && !hideDataNotice ? (
        <div className="strategy-details-data-notice" role="status">
          <span className="strategy-details-data-notice-icon" aria-hidden="true">
            <IconInfo size={16} color="rgba(129, 140, 248, 0.95)" />
          </span>
          <span className="strategy-details-data-notice-text">
            {vectorDataSource === 'ninjaAddon'
              ? 'Data syncs from the add-on in NinjaTrader. Third-party strategy data is for analytics only and may be historical, simulated, or model-based, not recommendations or guarantees.'
              : getDataSourceNote()}
          </span>
        </div>
      ) : null}
      {/* Summary Cards - Desktop: row1 = Best Day, Best Month, Winning Weeks, Net Total, Total Win Rate; row2 = Mon-Fri. Mobile: row1 = 4 cards, row2 = Mon-Fri + Total */}
      {/* Stats block: wraps both KPI rows (top + weekday) so a single frosted
          overlay can cover them for Basic-tier viewers. Collapsed by default
          for Basic users so calendar/chart content is visible without scrolling. */}
      {!viewerIsPro ? (
        <div className="strategy-stats-collapsible">
          <button
            type="button"
            className={`strategy-stats-collapsed-toggle${strategyStatsExpanded ? ' is-expanded' : ''}`}
            onClick={() => setStrategyStatsExpanded((expanded) => !expanded)}
            aria-expanded={strategyStatsExpanded}
            aria-controls="strategy-stats-locked-panel"
          >
            <span className="strategy-stats-collapsed-toggle-icon" aria-hidden="true">
              <IconLock size={16} />
            </span>
            <span className="strategy-stats-collapsed-toggle-text">
              You&apos;re missing Pro insights — best/worst days, win rates &amp; weekday breakdowns · tap to preview
            </span>
            <span
              className={`strategy-stats-collapsed-chevron${strategyStatsExpanded ? ' is-expanded' : ''}`}
              aria-hidden="true"
            >
              <IconChevronDown size={18} />
            </span>
          </button>
          {strategyStatsExpanded ? (
            <div
              id="strategy-stats-locked-panel"
              className="strategy-stats-block strategy-stats-block-locked"
            >
              <StrategyStatsLockedPreview />
              <div className="strategy-stats-lock-overlay" role="region" aria-label="Pro feature">
                <div className="strategy-stats-lock-card">
                  <div className="strategy-stats-lock-icon">
                    <IconLock size={28} />
                  </div>
                  <h3 className="strategy-stats-lock-title">Pro feature</h3>
                  <p className="strategy-stats-lock-copy">
                    Unlock detailed strategy stats — best / worst day, monthly totals,
                    winning weeks, win rate, and weekday breakdowns.
                  </p>
                  <button
                    type="button"
                    className="strategy-stats-lock-cta"
                    onClick={() => {
                      if (typeof onUpgrade === 'function') {
                        onUpgrade(`Upgrade to ${appName} Pro to unlock strategy stats and weekday breakdowns.`);
                      }
                    }}
                  >
                    Upgrade to {appName} Pro
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      ) : (
      <div className="strategy-stats-block">
      <>
      <div className="strategy-summary-cards strategy-summary-cards-top">
        <div className="strategy-summary-card card-best-day">
          <div className="strategy-summary-label strategy-summary-label--with-info">
            Best / Worst Day
            {showMetricsCompare && <FilterCompareInfoHint />}
          </div>
          <div className="strategy-summary-value stacked-values">
            <div className="stacked-line positive-inline">
              {formatCurrency(metrics?.bestDay?.value || 0, contractType, sheetName, contractSize)}
            </div>
            <div className={`stacked-line stacked-line-worst ${worstDayToneClass}`}>
              {formatCurrency(metrics?.worstDay?.value || 0, contractType, sheetName, contractSize)}
            </div>
          </div>
          <div className="strategy-summary-meta stacked-meta">
            <div className="positive-inline">
              Best: {metrics?.bestDay?.label} ({shortenDateYear(metrics?.bestDay?.date)})
            </div>
            <div className={worstDayToneClass}>
              Worst: {metrics?.worstDay?.label} ({shortenDateYear(metrics?.worstDay?.date)})
            </div>
          </div>
          {showMetricsCompare && (
            <>
              <div className="strategy-summary-stat-comparison">
                <span className="strategy-calendar-stat-vs">vs</span>
                <span className="strategy-calendar-stat-baseline positive-inline">
                  {formatCurrency(metricsBaseline.bestDay?.value || 0, contractType, sheetName, contractSize)}
                </span>
                <span className="strategy-calendar-stat-vs">/</span>
                <span className={`strategy-calendar-stat-baseline ${(metricsBaseline.worstDay?.value ?? 0) < 0 ? 'negative-inline' : 'positive-inline'}`}>
                  {formatCurrency(metricsBaseline.worstDay?.value || 0, contractType, sheetName, contractSize)}
                </span>
                <span className="strategy-calendar-stat-vs">all</span>
              </div>
              <div className="strategy-summary-stat-delta strategy-summary-stat-delta--pair">
                <span className={dayBestDelta >= 0 ? 'positive-inline' : 'negative-inline'}>
                  {formatSignedCurrencyDelta(dayBestDelta, formatExactCurrency)}
                </span>
                <span className="strategy-calendar-stat-vs">/</span>
                <span className={dayWorstDelta >= 0 ? 'positive-inline' : 'negative-inline'}>
                  {formatSignedCurrencyDelta(dayWorstDelta, formatExactCurrency)}
                </span>
              </div>
            </>
          )}
        </div>

        <div className="strategy-summary-card card-best-month">
          <div className="strategy-summary-label strategy-summary-label--with-info">
            Best / Worst Month
            {showMetricsCompare && <FilterCompareInfoHint />}
          </div>
          <div className="strategy-summary-value stacked-values">
            <div className="stacked-line positive-inline">
              {formatCurrency(metrics?.bestMonth?.value || 0, contractType, sheetName, contractSize)}
            </div>
            <div className={`stacked-line stacked-line-worst ${worstMonthToneClass}`}>
              {formatCurrency(metrics?.worstMonth?.value || 0, contractType, sheetName, contractSize)}
            </div>
          </div>
          <div className="strategy-summary-meta stacked-meta">
            <div className="positive-inline">
              Best: {formatMonthLabel(metrics?.bestMonth?.month)}
            </div>
            <div className={worstMonthToneClass}>
              Worst: {formatMonthLabel(metrics?.worstMonth?.month)}
            </div>
          </div>
          {showMetricsCompare && (
            <>
              <div className="strategy-summary-stat-comparison">
                <span className="strategy-calendar-stat-vs">vs</span>
                <span className="strategy-calendar-stat-baseline positive-inline">
                  {formatCurrency(metricsBaseline.bestMonth?.value || 0, contractType, sheetName, contractSize)}
                </span>
                <span className="strategy-calendar-stat-vs">/</span>
                <span className={`strategy-calendar-stat-baseline ${(metricsBaseline.worstMonth?.value ?? 0) < 0 ? 'negative-inline' : 'positive-inline'}`}>
                  {formatCurrency(metricsBaseline.worstMonth?.value || 0, contractType, sheetName, contractSize)}
                </span>
                <span className="strategy-calendar-stat-vs">all</span>
              </div>
              <div className="strategy-summary-stat-delta strategy-summary-stat-delta--pair">
                <span className={monthBestDelta >= 0 ? 'positive-inline' : 'negative-inline'}>
                  {formatSignedCurrencyDelta(monthBestDelta, formatExactCurrency)}
                </span>
                <span className="strategy-calendar-stat-vs">/</span>
                <span className={monthWorstDelta >= 0 ? 'positive-inline' : 'negative-inline'}>
                  {formatSignedCurrencyDelta(monthWorstDelta, formatExactCurrency)}
                </span>
              </div>
            </>
          )}
        </div>

        <div className="strategy-summary-card card-winning-weeks">
          <div className="strategy-summary-label strategy-summary-label--with-info">
            Winning / Losing Weeks
            {showMetricsCompare && <FilterCompareInfoHint />}
          </div>
          <div className="strategy-summary-value">
            <span className="positive-inline">{metrics?.winningWeeks || 0}</span>
            <span className="slash-inline">/</span>
            <span className="negative-inline">{metrics?.losingWeeks || 0}</span>
          </div>
          <div className="strategy-summary-meta">
            Totals &gt; 0 / Totals &lt; 0
          </div>
          {showMetricsCompare && (
            <>
              <div className="strategy-summary-stat-comparison">
                <span className="strategy-calendar-stat-vs">vs</span>
                <span className="strategy-calendar-stat-baseline positive-inline">{metricsBaseline.winningWeeks}</span>
                <span className="strategy-calendar-stat-vs">/</span>
                <span className="strategy-calendar-stat-baseline negative-inline">{metricsBaseline.losingWeeks}</span>
                <span className="strategy-calendar-stat-vs">all</span>
              </div>
              <div className="strategy-summary-stat-delta strategy-summary-stat-delta--pair">
                <span className={winWeekDelta >= 0 ? 'positive-inline' : 'negative-inline'}>
                  {formatSignedIntDelta(winWeekDelta)}
                </span>
                <span className="strategy-calendar-stat-vs">/</span>
                <span className={loseWeekDelta >= 0 ? 'positive-inline' : 'negative-inline'}>
                  {formatSignedIntDelta(loseWeekDelta)}
                </span>
              </div>
            </>
          )}
        </div>

        <div className="strategy-summary-card card-net-total">
          <div className="strategy-summary-label strategy-summary-label--with-info">
            Net Total
            {showMetricsCompare && <FilterCompareInfoHint />}
          </div>
          <div className={`strategy-summary-value ${((metrics?.totalWinsSum || 0) + (metrics?.totalLossSum || 0)) >= 0 ? 'positive' : 'negative'}`}>
            {formatCurrency((metrics?.totalWinsSum || 0) + (metrics?.totalLossSum || 0), contractType, sheetName, contractSize)}
          </div>
          {showMetricsCompare && (
            <>
              <div className="strategy-summary-stat-comparison">
                <span className="strategy-calendar-stat-vs">vs</span>
                <span
                  className={`strategy-calendar-stat-baseline ${((metricsBaseline.totalWinsSum || 0) + (metricsBaseline.totalLossSum || 0)) >= 0 ? 'positive' : 'negative'}`}
                >
                  {formatCurrency(
                    (metricsBaseline.totalWinsSum || 0) + (metricsBaseline.totalLossSum || 0),
                    contractType,
                    sheetName,
                    contractSize,
                  )}
                </span>
                <span className="strategy-calendar-stat-vs">all</span>
              </div>
              <div
                className={`strategy-summary-stat-delta ${netDelta >= 0 ? 'positive-inline' : 'negative-inline'}`}
              >
                {formatSignedCurrencyDelta(netDelta, formatExactCurrency)}
              </div>
            </>
          )}
          <div className="strategy-summary-meta stacked-meta">
            <div className="positive-inline">
              Wins: {formatCurrency(metrics?.totalWinsSum || 0, contractType, sheetName, contractSize)}
            </div>
            <div className="negative-inline">
              Losses: {formatCurrency(metrics?.totalLossSum || 0, contractType, sheetName, contractSize)}
            </div>
          </div>
        </div>

        <div className="strategy-summary-card card-total-win-rate strategy-summary-card-total-win-rate">
          <div className="strategy-summary-label strategy-summary-label--with-info">
            Total Win Rate
            {showMetricsCompare && <FilterCompareInfoHint />}
          </div>
          <div className={`strategy-summary-value ${(() => {
            const totalDays = metrics?.totalDays || 0;
            const winRate = totalDays > 0 ? ((metrics?.totalWinningDays || 0) / totalDays) * 100 : 0;
            return winRate >= 50 ? 'positive' : 'negative';
          })()}`}>
            {(() => {
              const totalDays = metrics?.totalDays || 0;
              const winRate = totalDays > 0 ? ((metrics?.totalWinningDays || 0) / totalDays) * 100 : 0;
              return winRate.toFixed(1);
            })()}%
          </div>
          {showMetricsCompare && (
            <>
              <div className="strategy-summary-stat-comparison">
                <span className="strategy-calendar-stat-vs">vs</span>
                <span className="strategy-calendar-stat-baseline">{baselineWinPct.toFixed(1)}%</span>
                <span className="strategy-calendar-stat-vs">all</span>
              </div>
              <div
                className={`strategy-summary-stat-delta ${totalWinRateDeltaPct >= 0 ? 'positive-inline' : 'negative-inline'}`}
              >
                {formatPctPointDelta(totalWinRateDeltaPct)}
              </div>
            </>
          )}
          <div className="strategy-summary-meta">
            {metrics?.totalWinningDays || 0} / {metrics?.totalDays || 0}
            {showMetricsCompare ? (
              <>
                {' '}
                <span className="strategy-calendar-stat-vs">vs</span>{' '}
                <span className="strategy-calendar-stat-baseline">
                  {metricsBaseline.totalWinningDays}/{metricsBaseline.totalDays}
                </span>
                <span className="strategy-calendar-stat-vs"> all days</span>
              </>
            ) : (
              ' days'
            )}
          </div>
        </div>
      </div>

      {/* Weekday Win Rates (Mon-Fri) - collapsed by default to keep the calendar
          high. The toggle renders BELOW the cards (just above the calendar) so
          expanding pushes content down rather than inserting between KPI rows. */}
      {weekdayStatsExpanded && (
      <div className="strategy-summary-cards strategy-summary-cards-weekday" id="strategy-weekday-stats">
        {[
          { key: 'Mon', label: 'Monday' },
          { key: 'Tue', label: 'Tuesday' },
          { key: 'Wed', label: 'Wednesday' },
          { key: 'Thu', label: 'Thursday' },
          { key: 'Fri', label: 'Friday' },
        ].map(({ key, label }) => {
          const pct = metrics?.weekdayWinRates?.[key] ?? 0;
          const basePct = metricsBaseline?.weekdayWinRates?.[key] ?? 0;
          const wdDeltaPct = pct - basePct;
          return (
            <div key={key} className="strategy-summary-card">
              <div className="strategy-summary-label strategy-summary-label--with-info">
                {label} Win Rate
                {showMetricsCompare && <FilterCompareInfoHint />}
              </div>
              <div className={`strategy-summary-value ${pct >= 50 ? 'positive' : 'negative'}`}>
                {pct.toFixed(1)}%
              </div>
              {showMetricsCompare && (
                <>
                  <div className="strategy-summary-stat-comparison">
                    <span className="strategy-calendar-stat-vs">vs</span>
                    <span className="strategy-calendar-stat-baseline">{basePct.toFixed(1)}%</span>
                    <span className="strategy-calendar-stat-vs">all</span>
                  </div>
                  <div
                    className={`strategy-summary-stat-delta ${wdDeltaPct >= 0 ? 'positive-inline' : 'negative-inline'}`}
                  >
                    {formatPctPointDelta(wdDeltaPct)}
                  </div>
                </>
              )}
              <div className="strategy-summary-meta">
                Wins / total days with data
              </div>
            </div>
          );
        })}
        {/* Mobile-only: Total Win Rate appears here (next to Friday) via duplicate + hide on desktop */}
        <div className="strategy-summary-card card-total-win-rate-mobile strategy-summary-card-total-win-rate">
          <div className="strategy-summary-label strategy-summary-label--with-info">
            Total Win Rate
            {showMetricsCompare && <FilterCompareInfoHint />}
          </div>
          <div className={`strategy-summary-value ${(() => {
            const totalDays = metrics?.totalDays || 0;
            const winRate = totalDays > 0 ? ((metrics?.totalWinningDays || 0) / totalDays) * 100 : 0;
            return winRate >= 50 ? 'positive' : 'negative';
          })()}`}>
            {(() => {
              const totalDays = metrics?.totalDays || 0;
              const winRate = totalDays > 0 ? ((metrics?.totalWinningDays || 0) / totalDays) * 100 : 0;
              return winRate.toFixed(1);
            })()}%
          </div>
          {showMetricsCompare && (
            <>
              <div className="strategy-summary-stat-comparison">
                <span className="strategy-calendar-stat-vs">vs</span>
                <span className="strategy-calendar-stat-baseline">{baselineWinPct.toFixed(1)}%</span>
                <span className="strategy-calendar-stat-vs">all</span>
              </div>
              <div
                className={`strategy-summary-stat-delta ${totalWinRateDeltaPct >= 0 ? 'positive-inline' : 'negative-inline'}`}
              >
                {formatPctPointDelta(totalWinRateDeltaPct)}
              </div>
            </>
          )}
          <div className="strategy-summary-meta">
            {metrics?.totalWinningDays || 0} / {metrics?.totalDays || 0}
            {showMetricsCompare ? (
              <>
                {' '}
                <span className="strategy-calendar-stat-vs">vs</span>{' '}
                <span className="strategy-calendar-stat-baseline">
                  {metricsBaseline.totalWinningDays}/{metricsBaseline.totalDays}
                </span>
                <span className="strategy-calendar-stat-vs"> all days</span>
              </>
            ) : (
              ' days'
            )}
          </div>
        </div>
      </div>
      )}

      <button
        type="button"
        className={`strategy-weekday-toggle${weekdayStatsExpanded ? ' is-expanded' : ''}`}
        onClick={() => setWeekdayStatsExpanded((v) => !v)}
        aria-expanded={weekdayStatsExpanded}
        aria-controls="strategy-weekday-stats"
      >
        <span>{weekdayStatsExpanded ? 'Hide weekday win rates' : 'Show weekday win rates (Mon–Fri)'}</span>
        <span className="strategy-weekday-toggle-chevron" aria-hidden="true">
          <IconChevronDown size={16} />
        </span>
      </button>

      </>
      </div>
      )}

      {/* Strategy Performance / Calendar */}
      <div className="strategy-weekly-performance">
        <div className="strategy-section-header">
          <h3>
            Strategy Performance{sheetName ? ` - ${sheetName}` : ''}
            {isInternal ? (
              <span
                className="strategy-internal-badge"
                title="Internal — visible to admins only. Members do not see this strategy anywhere."
              >
                Internal
              </span>
            ) : null}
          </h3>
          <div className="strategy-view-controls">
            <div className="strategy-toggle-row">
              {isNasdaqFuturesContractType(contractType) && (
                <span className="strategy-toggle-with-info">
                  <label className="strategy-roll-toggle">
                    <input
                      type="checkbox"
                      checked={showRollWeekIndicators}
                      onChange={(e) => {
                        const next = e.target.checked;
                        setShowRollWeekIndicators(next);
                        try {
                          localStorage.setItem(STORAGE_KEY_ROLL, next ? '1' : '0');
                        } catch (_) {}
                        try {
                          window.dispatchEvent(new Event('risklo-roll-indicators-changed'));
                        } catch (_) {}
                      }}
                    />
                    <span>Show Rollover Week</span>
                  </label>
                  <button
                    type="button"
                    className="strategy-toggle-info-btn"
                    aria-label="Roll week info"
                    onClick={() => setRollInfoModalOpen(true)}
                  >
                    <IconInfo size={14} />
                  </button>
                  <PnlScalingInfoModal
                    open={rollInfoModalOpen}
                    onClose={() => setRollInfoModalOpen(false)}
                    title="Futures roll and contract rollover"
                    paragraphs={getRollWeekModalParagraphs()}
                  />
                </span>
              )}
              {RED_FOLDER_FILTER_ENABLED && (
              <span className="strategy-toggle-with-info">
                <label className="strategy-roll-toggle strategy-high-impact-toggle">
                  <input
                    type="checkbox"
                    checked={showHighImpactUsd}
                    onChange={(e) => {
                      const next = e.target.checked;
                      setShowHighImpactUsd(next);
                      try {
                        localStorage.setItem(STORAGE_KEY_HIGH_IMPACT, next ? '1' : '0');
                      } catch (_) {}
                    }}
                  />
                  <span>Show Red Folder News</span>
                </label>
                <button
                  type="button"
                  className="strategy-toggle-info-btn"
                  aria-label="USD high-impact info"
                  onClick={() => setHighImpactInfoModalOpen(true)}
                >
                  <IconInfo size={14} />
                </button>
                <PnlScalingInfoModal
                  open={highImpactInfoModalOpen}
                  onClose={() => setHighImpactInfoModalOpen(false)}
                  title="USD high-impact calendar days"
                  paragraphs={getHighImpactModalParagraphs()}
                />
              </span>
              )}
            </div>
            <div className="strategy-view-toggle">
              <button
                className={`strategy-view-btn ${viewMode === 'calendar' ? 'active' : ''}`}
                onClick={() => {
                  setViewMode('calendar');
                  try { localStorage.setItem(STORAGE_KEY_VIEW, 'calendar'); } catch (_) {}
                }}
              >
                Calendar
              </button>
              <button
                className={`strategy-view-btn ${viewMode === 'chart' ? 'active' : ''}`}
                onClick={() => {
                  setViewMode('chart');
                  try { localStorage.setItem(STORAGE_KEY_VIEW, 'chart'); } catch (_) {}
                }}
              >
                Chart
              </button>
              <button
                className={`strategy-view-btn ${viewMode === 'weekly' ? 'active' : ''}`}
                onClick={() => {
                  setViewMode('weekly');
                  try { localStorage.setItem(STORAGE_KEY_VIEW, 'weekly'); } catch (_) {}
                }}
              >
                Weekly
              </button>
              <button
                className={`strategy-view-btn ${viewMode === 'spreadsheet' ? 'active' : ''}`}
                onClick={() => {
                  setViewMode('spreadsheet');
                  try { localStorage.setItem(STORAGE_KEY_VIEW, 'spreadsheet'); } catch (_) {}
                }}
              >
                Spreadsheet
              </button>
            </div>
          </div>
        </div>

        {viewMode !== 'spreadsheet' &&
          (viewMode === 'weekly' ||
            (viewMode === 'chart' && vectorDateRangeFilter != null && onVectorDateRangeChange)) && (
          <div className="strategy-chart-vector-toolbar">
            <div className="strategy-chart-vector-toolbar-inner">
              <div className="strategy-chart-vector-toolbar-left">
                {vectorDateRangeFilter != null && onVectorDateRangeChange && (
                  <div className="strategy-chart-vector-toolbar-field">
                    <label htmlFor="strategy-chart-vector-date-range" className="results-select-label">
                      Date Range
                    </label>
                    <select
                      id="strategy-chart-vector-date-range"
                      className="results-date-range-select strategy-chart-vector-date-select"
                      aria-label="Date range (same as Results filter above)"
                      value={vectorDateRangeFilter}
                      onChange={(e) => onVectorDateRangeChange(e.target.value)}
                    >
                      <option value="all-time">All Time</option>
                      <option value="last-30-days">Last 30 Days</option>
                      <option value="last-3-months">Last 3 Months</option>
                      <option value="last-6-months">Last 6 Months</option>
                      <option value="last-12-months">Last 12 Months</option>
                      <option value="2024">2024</option>
                      <option value="2025">2025</option>
                      <option value="custom">Custom Range...</option>
                    </select>
                  </div>
                )}
                <StrategyFilterCompareControl
                  displayFilter={displayFilter}
                  onDisplayFilterChange={setDisplayFilter}
                  rollEnabled={showRollWeekIndicators && isNasdaqFuturesContractType(contractType)}
                  wrapClassName="strategy-chart-vector-filter-wrap"
                />
              </div>
              <div
                className="strategy-chart-vector-toolbar-stats strategy-calendar-month-stats"
                aria-label={
                  viewMode === 'weekly'
                    ? 'Filtered vs all net total for weeks in this list'
                    : 'Net total for selected date range'
                }
              >
                <span className="strategy-calendar-stat-label strategy-calendar-stat-label--with-info">
                  {viewMode === 'weekly' ? 'Week list stats' : 'Date range stats'}
                  {chartDateRangeStats.filterActive ? <FilterCompareInfoHint /> : null}
                </span>
                {chartDateRangeStats.filterActive ? (
                  <>
                    <span
                      className={`strategy-calendar-stat-value ${
                        (chartDateRangeStats.totalFiltered ?? 0) >= 0 ? 'positive' : 'negative'
                      }`}
                    >
                      {formatExactCurrency(chartDateRangeStats.totalFiltered ?? 0)}
                    </span>
                    <span className="strategy-calendar-stat-comparison">
                      <span className="strategy-calendar-stat-vs">vs</span>
                      <span
                        className={`strategy-calendar-stat-baseline ${
                          (chartDateRangeStats.totalAll ?? 0) >= 0 ? 'positive' : 'negative'
                        }`}
                      >
                        {formatExactCurrency(chartDateRangeStats.totalAll ?? 0)}
                      </span>
                      <span className="strategy-calendar-stat-vs">all</span>
                    </span>
                    <span
                      className={`strategy-calendar-stat-delta ${
                        (chartDateRangeStats.totalFiltered ?? 0) - (chartDateRangeStats.totalAll ?? 0) >= 0
                          ? 'positive'
                          : 'negative'
                      }`}
                    >
                      {formatSignedCurrencyDelta(
                        (chartDateRangeStats.totalFiltered ?? 0) - (chartDateRangeStats.totalAll ?? 0),
                        formatExactCurrency,
                      )}
                    </span>
                    <span className="strategy-calendar-stat-days">
                      {chartDateRangeStats.daysFiltered ?? 0} / {chartDateRangeStats.daysAll ?? 0} days
                    </span>
                  </>
                ) : (
                  <>
                    <span
                      className={`strategy-calendar-stat-value ${
                        (chartDateRangeStats.totalAll ?? 0) >= 0 ? 'positive' : 'negative'
                      }`}
                    >
                      {formatExactCurrency(chartDateRangeStats.totalAll ?? 0)}
                    </span>
                    <span className="strategy-calendar-stat-days">{chartDateRangeStats.daysAll ?? 0} days</span>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {viewMode === 'spreadsheet' ? (
          <SpreadsheetView
            rawRows={rawRows}
            sort={spreadsheetSort}
            onSortChange={setSpreadsheetSort}
          />
        ) : viewMode === 'chart' ? (
          <StrategyCumulativeChart
            chartData={chartData}
            formatCurrencyFull={formatScaledCurrency}
            formatAxisCurrency={formatScaledCurrency}
            contractType={contractType}
            showRollWeek={showRollWeekIndicators}
            highImpactEventsByDate={highImpactEventsByDate}
            showHighImpactUsd={showHighImpactUsd}
            liveSince={journey?.view === 'full' ? journey.liveSince : null}
          />
        ) : viewMode === 'calendar' ? (
          <StrategyCalendarView
            calendarMonth={calendarMonth}
            setCalendarMonth={setCalendarMonth}
            calendarRange={calendarRange}
            setCalendarRange={setCalendarRange}
            dailyData={dailyData}
            monthWeeksSummary={monthWeeksSummary}
            calendarMonthStats={calendarMonthStats}
            yearStats={yearStats}
            formatCurrencyValue={formatExactCurrency}
            contractType={contractType}
            showRollWeek={showRollWeekIndicators}
            highImpactEventsByDate={highImpactEventsByDate}
            showHighImpactUsd={showHighImpactUsd}
            impactFilterValue={impactFilterValue}
            displayFilter={displayFilter}
            onDisplayFilterChange={setDisplayFilter}
            filteredCalendarMonthStats={filteredCalendarMonthStats}
            filteredYearCalendarStats={filteredYearCalendarStats}
            filteredMonthWeeksSummary={filteredMonthWeeksSummary}
            sheetName={sheetName}
          />
        ) : (
          <div className="strategy-weeks-grid">
            {weeklyGridDisplay.map(({ week, fw, baselineDaysCount }, idx) => {
              const displayTotal = fw ? fw.filteredTotal : week.total;
              const rollWeekCard =
                showRollWeekIndicators &&
                isNasdaqFuturesContractType(contractType) &&
                nasdaqRollWeekTouchesWeekMonday(week.date);
              const highImpactWeekCard =
                showHighImpactUsd &&
                highImpactEventsByDate &&
                highImpactTouchesWeekMonday(week.date, highImpactEventsByDate);
              return (
              <div
                key={idx}
                role="button"
                tabIndex={0}
                className={`strategy-week-card strategy-week-card--clickable ${displayTotal >= 0 ? 'win' : 'loss'}${rollWeekCard ? ' strategy-week-card--roll-week' : ''}`}
                aria-label={`Open week details for ${formatWeekLabel(week.date)}`}
                onClick={() => setWeeklyViewWeekPeekMonday(week.date)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setWeeklyViewWeekPeekMonday(week.date);
                  }
                }}
              >
                <div className="strategy-week-header">
                  <span className="strategy-week-date-row">
                    <span className="strategy-week-date">{formatWeekLabel(week.date)}</span>
                    <span className="strategy-week-header-badges">
                      {rollWeekCard && (
                        <span
                          className="strategy-week-roll-badge"
                          title="This week overlaps CME MNQ/NQ roll week (week of quarterly last trading day)"
                        >
                          <IconContractRoll size={14} color="rgba(167, 139, 250, 0.95)" />
                        </span>
                      )}
                      {highImpactWeekCard && (
                        <span
                          className="strategy-week-high-impact-badge"
                          title="This week includes a USD high-impact release day (economic calendar)"
                        >
                          <IconHighImpactFolder size={14} color="#ef4444" />
                        </span>
                      )}
                    </span>
                  </span>
                  <div className="strategy-week-header-totals">
                    <span className={`strategy-week-total ${displayTotal >= 0 ? 'positive' : 'negative'}`}>
                      {formatCurrency(displayTotal, contractType, sheetName, contractSize)}
                    </span>
                    {fw && fw.filteredTotal !== week.total && (
                      <div className="strategy-week-card-filter-compare">
                        <span className="strategy-calendar-week-baseline">
                          vs {formatCurrency(week.total, contractType, sheetName, contractSize)} all
                        </span>
                        <span
                          className={`strategy-calendar-stat-delta ${
                            fw.filteredTotal - week.total >= 0 ? 'positive' : 'negative'
                          }`}
                        >
                          {formatSignedCurrencyDelta(
                            fw.filteredTotal - week.total,
                            formatExactCurrency,
                          )}
                        </span>
                      </div>
                    )}
                    {fw && (
                      <span className="strategy-week-card-days-meta">
                        {fw.filteredDays} / {baselineDaysCount} day{baselineDaysCount !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
                <div className="strategy-week-days">
                  {DAY_ORDER.map((day) => {
                    const rawVal = week.days[day] ?? 0;
                    const displayVal = fw ? fw.filteredDayPnl[day] ?? 0 : rawVal;
                    const excludedByFilter = !!fw && rawVal !== 0 && displayVal === 0;
                    const valClass =
                      excludedByFilter && displayVal === 0
                        ? 'strategy-day-value strategy-day-value--filter-excluded'
                        : `strategy-day-value ${displayVal >= 0 ? 'positive' : 'negative'}`;
                    return (
                      <div
                        key={day}
                        className={`strategy-day-item${excludedByFilter ? ' strategy-day-item--filter-excluded' : ''}`}
                      >
                        <span className="strategy-day-label">{day}</span>
                        <span className={valClass}>
                          {formatCurrency(displayVal, contractType, sheetName, contractSize)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {weeklyViewWeekPeekMonday && weekPeekDetailFromWeekly && (
        <StrategyWeekPeekPortal
          weekPeekDetail={weekPeekDetailFromWeekly}
          onClose={() => setWeeklyViewWeekPeekMonday(null)}
          formatCurrencyValue={formatExactCurrency}
          expandedNewsRows={weeklyWeekPeekExpandedNewsRows}
          setExpandedNewsRows={setWeeklyWeekPeekExpandedNewsRows}
          showHighImpactUsd={showHighImpactUsd}
        />
      )}
    </div>
  );
}

export default StrategyDetailsView;

